import type {
  CaseManifestV1,
  LayerANpcPatrol,
  LayerASpec,
  LayerAWalkStrip,
  LayerAAccentPlacement,
} from "@cold-case/shared/case-manifest";
import {
  BUILDING_FOOTPRINT_TILES,
  INTERIOR_EXIT_INTERACT_TILES,
  INTERIOR_GRID_H,
  INTERIOR_GRID_W,
  LAYER_A_GRID_H,
  LAYER_A_GRID_W,
  LAYER_A_MARGIN_TILES,
  NPC_CONGREGATION_MIN_TILE_DIST,
  NPC_DIRECTIONS,
  NPC_INTERACT_RADIUS_TILES,
  PLAYER_IDLE_KEEPALIVE_MS,
  TILE_SIZE,
  WORLD_CHARACTER_DISPLAY_PX,
} from "@cold-case/shared/constants";
import type { BuildingInteriorSpec } from "@cold-case/shared/interior-layout";
import { nearestSpineIndex, nearestSpineVertex } from "@cold-case/shared/layer-walk";
import Phaser from "phaser";
import type { Socket } from "socket.io-client";

import {
  PLAYFIELD_WALK_STEER_HOLD_MS,
  RUN_CAMERA_FOLLOW_LERP_SOFT,
  RUN_CAMERA_FOLLOW_LERP_X,
  RUN_CAMERA_FOLLOW_LERP_Y,
  RUN_MAIN_CAMERA_ZOOM,
  RUN_PLAYER_SPEED_PX_S,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from "./design-resolution.js";
import {
  buildingTextureUrl,
  npcDirectionTextureKey,
  npcRotationTexturePathRotations,
} from "../lib/asset-mapper.js";
import {
  resolveBuildingDisplayName,
  VACANT_PROPERTY_LABEL,
} from "../lib/building-display.js";
import { buildingEntryArrowInteractHint } from "../content/building-permission-copy.js";
import {
  buildingAnchorFromSprite,
  buildingMaskWorldBounds,
  BUILDING_FOOTPRINT_ALPHA_TOLERANCE,
  type BuildingOpaqueAlphaSample,
  worldPointHitsBuildingOpaqueMask,
} from "./building-art-footprint.js";
import { buildingInteractStandPoint } from "./building-interact-stand.js";
import {
  setBuildingArtTexture,
  type BuildingArtTint,
} from "./building-art-sprite.js";
import {
  cleanAllBuildingTextures,
} from "./building-texture-cleanup.js";
import { layoutClueToastIfVisible } from "./clue-toast.js";
import { layoutInteractHint } from "./interact-hint-layout.js";
import {
  buildInteriorContentPlan,
  type BuildingExitStand,
  type InteriorContentPlan,
} from "./building-interior-flow.js";
import { spawnClueMarker, playerNearClueMarker } from "./interior-content.js";
import { clueMarkerWorldPoint } from "./interior-exit-world.js";
import { interiorNpcTile } from "@cold-case/shared/interior-layout";
import { pickInteriorPatrolTarget } from "./interior-patrol.js";
import { npcPatrolRng, randIntBetween } from "../lib/seeded-rng.js";
import {
  interiorExitArrowRotation,
  interiorExitArrowWorldPoint,
  interiorExitConfirmMessage,
  interiorExitTileCenter,
  tileToWorldCenter,
} from "./interior-exit-world.js";
import { InteriorMapGrid } from "./interior-map-grid.js";
import { isImageTextureQueued } from "./texture-load-queue.js";
import { MapGrid, resolveWalkStrip, type ZoneId } from "./map-grid.js";
import {
  ensureTransitChevronTexture,
  INTERIOR_EXIT_CHEVRON_PALETTE,
  ZONE_TRANSIT_CHEVRON_PALETTE,
} from "./transit-chevron.js";
import { TILES_H, TILES_W } from "./map-world-dims.js";
import {
  buildingMapCenter,
  collectMinimapPlayerBlips,
  isValidWorldWaypoint,
  type MapWaypointTarget,
  type MinimapPlayerBlip,
} from "./case-files-map.js";
import { nearestWalkableTile, slideToNearestPassable } from "./walk-target.js";
import { createWaypointArrow, type WaypointArrowApi } from "./waypoint-arrow.js";
import {
  zoneTransitArrowRotation,
  zoneTransitArrowWorldPoint,
  zoneTransitConfirmMessage,
  zoneTransitDestination,
  zoneTransitTile,
} from "./zone-transit-world.js";
import {
  closeZoneTransitConfirm,
  isZoneTransitConfirmOpen,
  openZoneTransitConfirm,
} from "../lib/zone-transit-confirm.js";
import { isFollowWorldStateSynced } from "./follow-world-sync.js";
import { isFollowConfirmOpen } from "../lib/follow-confirm.js";
import { isNarrativeModalOpen } from "../lib/narrative-modal.js";
import {
  isNpcConversationOverlayOpen,
  isPlayerInputFrozen,
  shouldPhaserReadMovementKeys,
} from "../lib/player-input-gate.js";
import {
  PLAYER_POSE_MIN_INTERVAL_MS,
  PLAYER_POSE_MIN_MOVE_PX,
} from "@cold-case/shared/player-pose";
import {
  bindPeerSync,
  clearAllPeers,
  findPeerUserIdAtWorldPoint,
  getPeerFacing,
  getPeerRig,
  seedPeersFromSession,
  setPeerZoneVisibility,
  snapFollowersToLeaderExit,
  tickPeerInterpolation,
  type PeerState,
  type PeerSyncHost,
} from "./peer-sync.js";
import { peerTapBlockedMessage } from "../lib/peer-tap-blocked.js";
import type { CoherenceJournal } from "../lib/dialog-parser.js";
import {
  destroyBuildingEntryArrows,
  entryArrowInteractMaxDistPx,
  entryArrowStandWorldPoint,
  findBuildingEntryArrowAt,
  syncBuildingEntryArrows,
  type BuildingEntryArrowState,
} from "./building-entry-arrow.js";
import {
  isFollowingLeader,
  onPeerTapped,
  rebindPlayerFollowSocket,
  tickFollowMovement,
} from "./player-follow.js";
import { emitLeaderMirror, getLeaderFollowerUserIds } from "./leader-follow-mirror.js";
import type { GameZoneId } from "@cold-case/shared/player-pose";
import {
  ensureGeneratedTexture,
} from "./reset-case-runtime.js";
import { velocityToPlayableDir, type PlayableDir } from "./playable-facing.js";
import { updateUnstuckMotion } from "./unstuck-offer.js";
import { UI_FONT, UI_TEXT } from "./ui-theme.js";
import {
  setWorldCharacterPlaceholder,
  setWorldCharacterTexture,
  worldCharacterTapRadiusPx,
  worldCharacterWalkBobY,
  WORLD_CHARACTER_BOB_SPEED,
} from "./world-character-sprite.js";
import {
  createWorldNameplateText,
  npcWorldNameplateLabel,
  syncNameplateAboveSprite,
  worldLabelForPlayer,
} from "./world-nameplate.js";
import {
  pendingInteractInRange,
  playerNearZoneTransit,
  type PendingWorldInteract,
  type WorldInteractRangeCtx,
} from "./world-interact-range.js";

type Reg = {
  sessionId: string;
  token: string;
  caseManifest: CaseManifestV1;
  layerA: LayerASpec;
  socket: Socket | null;
  userId: string;
  playerName: string;
  /** Lobby "detective" skin — same art pipeline as NPCs (`/assets/npcs/...`). */
  playerCharacterNpcId: string;
  players: {
    userId: string;
    displayName: string;
    characterNpcId: string;
    zone?: string;
    tileX?: number;
    tileY?: number;
  }[];
  /** Lobby size — matches manifest `player_count` for dialog conditions. */
  playerCount: number;
  /** Seconds already elapsed on server (`startedAt`) when this scene booted. */
  timerElapsedOffsetSeconds: number;
  /** When resuming a session, restore the neighborhood the player was in. */
  resumeZone?: ZoneId;
  bootMeta?: { alreadyResolved: boolean };
  onJournalPatch: (path: string, value: unknown) => void;
  getJournal: () => Record<string, unknown>;
};

type PendingInteraction = PendingWorldInteract;

const RUN_NPC_PATROL_SPEED_PX_S = 72;
const FILLER_AGGRESSIVE_PATROL_SPEED_PX_S = 96;
const NPC_PATROL_PAUSE_MIN_MS = 700;
const NPC_PATROL_PAUSE_MAX_MS = 2600;
const FILLER_AGGRESSIVE_PATROL_PAUSE_MIN_MS = 500;
const FILLER_AGGRESSIVE_PATROL_PAUSE_MAX_MS = 1200;
const NPC_SEPARATION_PX = 44;
const FILLER_CONGREGATION_SEPARATION_PX =
  NPC_CONGREGATION_MIN_TILE_DIST * TILE_SIZE;
const MIN_VEL_EPS = 12;
const TRANSIT_INTERACT_TILES = 2.6;
/** Tap must land on the NPC sprite — not a large radius from the click point. */
const NPC_TAP_HIT_RADIUS_PX = worldCharacterTapRadiusPx(0.52);
const BUILDING_PLACEHOLDER_W = 128;
const BUILDING_PLACEHOLDER_H = 160;
const SPEED_BLEND_MS = 380;
type NpcRuntime = {
  rig: Phaser.GameObjects.Container;
  vis: Phaser.GameObjects.Sprite;
  nameplate: Phaser.GameObjects.Text;
  patrolTargetX: number;
  patrolTargetY: number;
  patrolPauseMs: number;
  patrolPauseMinMs: number;
  patrolPauseMaxMs: number;
  patrolUnlockAtMs: number;
  npcSkinId: string;
  lastFacingDir: PlayableDir;
  faceTowardWx?: number;
  faceTowardWy?: number;
  /** Patrol on interior walkable tiles (not outdoor spine). */
  interiorPatrol?: boolean;
  /** Filler NPC patrol tuning from Layer A segment. */
  wanderStyle?: "default" | "aggressive" | "stationary";
  congregationGroupId?: string;
  bobPhase: number;
  /** Increments per patrol pick for deterministic RNG. */
  patrolPickSeq: number;
};

export class GameScene extends Phaser.Scene {
  private deferredAssetsStarted = false;
  private npcArtRefreshHooked = false;
  private zonesWithDeferredBuildings = new Set<ZoneId>();
  /** Per-texture alpha rows for building collision (matches pixel-perfect click hits). */
  private buildingTextureAlphaRows = new Map<
    string,
    { width: number; height: number; alpha: Uint8ClampedArray }
  >();

  preload(): void {
    ensureGeneratedTexture(this, "placeholder_npc", 64, 64, (g) => {
      g.fillStyle(0x353540, 1);
      g.fillRoundedRect(2, 2, 60, 60, 10);
      g.lineStyle(2, 0x5a5a6e, 1);
      g.strokeRoundedRect(2, 2, 60, 60, 10);
      g.fillStyle(0x7a7a90, 1);
      g.fillCircle(32, 26, 12);
      g.fillRoundedRect(18, 38, 28, 22, 6);
    });
    ensureGeneratedTexture(
      this,
      "placeholder_bld",
      BUILDING_PLACEHOLDER_W,
      BUILDING_PLACEHOLDER_H,
      (g) => {
        g.fillStyle(0x2a221c, 1);
        g.fillRect(0, 0, BUILDING_PLACEHOLDER_W, BUILDING_PLACEHOLDER_H);
        g.fillStyle(0x4a3d32, 1);
        g.fillRect(8, 28, BUILDING_PLACEHOLDER_W - 16, BUILDING_PLACEHOLDER_H - 36);
        g.fillStyle(0x6a5648, 1);
        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 3; col++) {
            g.fillRect(18 + col * 32, 44 + row * 32, 20, 22);
          }
        }
        g.lineStyle(3, 0x8a7058, 1);
        g.strokeRect(8, 28, BUILDING_PLACEHOLDER_W - 16, BUILDING_PLACEHOLDER_H - 36);
      }
    );

    const reg = this.registry.get("ccc") as Reg | undefined;
    if (!reg) return;
    const bootZone = this.bootOutdoorZone(reg);
    for (const npcId of this.allNpcSkinIds(reg)) {
      const key = npcDirectionTextureKey(npcId, "south");
      this.queueTextureIfMissing(key, npcRotationTexturePathRotations(npcId, "south"));
    }
    for (const bid of this.buildingIdsForZone(reg, bootZone)) {
      const key = `bld_art_${bid}`;
      this.queueTextureIfMissing(key, buildingTextureUrl(bid));
    }
  }

  /** Outdoor zone for first boot — matches initialOutdoorSpawn / create. */
  private bootOutdoorZone(reg: Reg): ZoneId {
    if (reg.resumeZone) return reg.resumeZone;
    return this.startZoneFromLayerA(reg.layerA);
  }

  private startZoneFromLayerA(layerA: LayerASpec): ZoneId {
    return layerA.player_spawn?.zone === "maple_heights" ? "maple_heights" : "downtown";
  }

  private allNpcSkinIds(reg: Reg): Set<string> {
    const ids = new Set<string>();
    this.addNpcSkinId(ids, reg.playerCharacterNpcId);
    for (const p of reg.players) this.addNpcSkinId(ids, p.characterNpcId);
    for (const n of reg.caseManifest.npcs) this.addNpcSkinId(ids, n.npc_id);
    return ids;
  }

  private addNpcSkinId(ids: Set<string>, npcId: string | undefined): void {
    if (typeof npcId !== "string" || !npcId.trim()) return;
    ids.add(npcId.trim());
  }

  private npcSkinIdsForZone(reg: Reg, zone: ZoneId): Set<string> {
    const ids = new Set<string>();
    for (const pl of reg.layerA.npc_placements ?? []) {
      if (pl.zone !== zone) continue;
      const n = reg.caseManifest.npcs.find((x) => x.instance_id === pl.id);
      if (n) ids.add(n.npc_id);
    }
    return ids;
  }

  private buildingIdsForZone(reg: Reg, zone: ZoneId): Set<string> {
    const ids = new Set<string>();
    for (const pl of reg.layerA.building_placements ?? []) {
      if (pl.zone !== zone) continue;
      if (typeof pl.id !== "string" || !pl.id.trim()) continue;
      ids.add(pl.id.trim());
    }
    return ids;
  }

  private queueTextureIfMissing(key: string, url: string): boolean {
    if (this.isTextureReady(key)) return false;
    if (isImageTextureQueued(this.load, key)) return false;
    if (this.textures.exists(key)) return false;
    this.load.image(key, url);
    return true;
  }

  private scheduleNpcArtRefreshOnLoad(): void {
    if (this.npcArtRefreshHooked) return;
    this.npcArtRefreshHooked = true;
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.npcArtRefreshHooked = false;
      this.refreshArtTextures();
    });
    if (!this.load.isLoading()) this.load.start();
  }

  private queueMissingNpcTextures(
    npcIds: Iterable<string>,
    dirs: readonly PlayableDir[] = NPC_DIRECTIONS
  ): boolean {
    let queued = false;
    for (const npcId of npcIds) {
      for (const dir of dirs) {
        const key = npcDirectionTextureKey(npcId, dir);
        if (this.queueTextureIfMissing(key, npcRotationTexturePathRotations(npcId, dir))) {
          queued = true;
        }
      }
    }
    if (queued) this.scheduleNpcArtRefreshOnLoad();
    return queued;
  }

  private startDeferredAssetLoad(): void {
    if (this.deferredAssetsStarted) return;
    this.deferredAssetsStarted = true;
    const reg = this.reg();
    const startZone = this.zone;
    const otherZone: ZoneId = startZone === "downtown" ? "maple_heights" : "downtown";
    let queued = false;

    for (const npcId of this.allNpcSkinIds(reg)) {
      for (const dir of NPC_DIRECTIONS) {
        const key = npcDirectionTextureKey(npcId, dir);
        if (this.queueTextureIfMissing(key, npcRotationTexturePathRotations(npcId, dir))) {
          queued = true;
        }
      }
    }

    for (const bid of this.buildingIdsForZone(reg, otherZone)) {
      const key = `bld_art_${bid}`;
      if (this.queueTextureIfMissing(key, buildingTextureUrl(bid))) queued = true;
    }
    this.zonesWithDeferredBuildings.add(otherZone);

    if (!queued) return;
    this.scheduleNpcArtRefreshOnLoad();
  }

  private ensureZoneBuildingArt(zone: ZoneId): void {
    if (this.zonesWithDeferredBuildings.has(zone)) return;
    const reg = this.reg();
    let queued = false;
    for (const bid of this.buildingIdsForZone(reg, zone)) {
      const key = `bld_art_${bid}`;
      if (this.queueTextureIfMissing(key, buildingTextureUrl(bid))) queued = true;
    }
    this.zonesWithDeferredBuildings.add(zone);
    if (!queued) return;
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.refreshBuildingArtTextures();
    });
    if (!this.load.isLoading()) this.load.start();
  }

  private refreshArtTextures(): void {
    if (this.playerVis) {
      this.applyNpcTexture(this.playerVis, this.reg().playerCharacterNpcId, this.lastPlayerDir);
    }
    for (const run of this.npcSprites.values()) {
      this.applyNpcTexture(run.vis, run.npcSkinId, run.lastFacingDir);
    }
    this.refreshBuildingArtTextures();
    if (this.peerHost) {
      for (const peer of this.peerHost.peers.values()) {
        this.applyNpcTexture(peer.vis, peer.characterNpcId, peer.lastDir);
      }
    }
  }

  private refreshBuildingArtTextures(): void {
    for (const spr of this.buildingSprites.values()) {
      setBuildingArtTexture(spr, "placeholder_bld", "placeholder");
    }
    cleanAllBuildingTextures(this);
    this.clearBuildingTextureAlphaCache();
    for (const spr of this.buildingSprites.values()) {
      const bid = spr.getData("building_id") as string;
      if (!bid) continue;
      const key = this.buildingArtKey(bid);
      const b = this.reg().caseManifest.buildings.find((x) => x.building_id === bid);
      const tint: BuildingArtTint =
        key === "placeholder_bld" ? "placeholder" : b?.is_crime_scene ? "crime_scene" : "none";
      setBuildingArtTexture(spr, key, tint);
    }
  }

  /** Strip backdrop mats and re-apply art on outdoor building sprites after zone respawn. */
  private finalizeOutdoorBuildingArt(): void {
    if (this.buildingSprites.size === 0) return;
    this.refreshBuildingArtTextures();
  }

  private zone: ZoneId = "downtown";
  /** Outdoor neighborhood while exploring interiors (for labels + restore). */
  private outdoorZone: ZoneId = "downtown";
  private viewMode: "outdoor" | "interior" = "outdoor";
  private interiorBuildingId: string | null = null;
  private interiorSpec: BuildingInteriorSpec | null = null;
  private interiorContentPlan: InteriorContentPlan | null = null;
  private interiorMapGrid: InteriorMapGrid | null = null;
  private interiorClueMarker: Phaser.GameObjects.Image | null = null;
  private savedExitStand: BuildingExitStand | null = null;
  private mapGrid!: MapGrid;
  private ground!: Phaser.GameObjects.Container;
  private zoneTransitTimer?: Phaser.Time.TimerEvent;
  private idleKeepaliveTimer?: Phaser.Time.TimerEvent;
  private transitDecor: Phaser.GameObjects.GameObject[] = [];
  private accentVfxObjects: Phaser.GameObjects.GameObject[] = [];
  private playerRig!: Phaser.GameObjects.Container;
  private playerVis!: Phaser.GameObjects.Sprite;
  private playerNameplate!: Phaser.GameObjects.Text;
  private target = new Phaser.Math.Vector2();
  private hasTarget = false;
  private npcSprites = new Map<string, NpcRuntime>();
  private buildingSprites = new Map<string, Phaser.GameObjects.Sprite>();
  private buildingEntryArrows = new Map<string, BuildingEntryArrowState>();
  private npcPatrolByInstance = new Map<string, LayerANpcPatrol>();
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyE?: Phaser.Input.Keyboard.Key;
  private elapsed = 0;
  private transitCooldown = 0;
  private interactHint!: Phaser.GameObjects.Text;
  private bobPhase = 0;
  private lastPlayerDir: PlayableDir = "south";
  private poseSeq = 0;
  private lastPoseEmitAtMs = 0;
  private lastPoseEmitX = 0;
  private lastPoseEmitY = 0;
  private lastPoseEmitFacing: PlayableDir = "south";
  private lastPoseEmitZone: ZoneId = "downtown";
  private lastSafePlayer = new Phaser.Math.Vector2();
  private cameraLerpUntil = 0;
  /** False from zone transition until `cameraLerpUntil`, when we ease back to normal follow lerp. */
  private cameraFollowRelaxed = true;
  private walkZone!: Phaser.GameObjects.Zone;
  private walkPointerId: number | null = null;
  private walkSteerActive = false;
  private walkDownAtMs = 0;
  private activeDialogNpcId: string | null = null;
  private waypointArrow?: WaypointArrowApi;
  private partnerWaypointArrow?: WaypointArrowApi;
  private mapWaypoint: MapWaypointTarget | null = null;
  private walkSteerDragPx = 0;
  private onJournalOpen = (_open: boolean): void => {};
  private readonly onMapWaypointBound = (p: MapWaypointTarget | null): void => {
    this.setMapWaypoint(p);
  };
  private readonly onPartnerMapWaypointBound = (p: MapWaypointTarget | null): void => {
    const next = p && isValidWorldWaypoint(p) ? p : null;
    this.partnerWaypointArrow?.setTarget(next ? { wx: next.wx, wy: next.wy } : null);
  };
  private lastMovementAtMs = 0;
  private lastIdleSampleX = 0;
  private lastIdleSampleY = 0;
  private unstuckOffered = false;
  private caseFrozen = false;
  private accusationArrestActive = false;
  private accusationPhaseActive = false;
  private followCameraTarget: Phaser.GameObjects.Container | null = null;
  private followWorldSyncing = false;
  private followWorldSyncQueue: Promise<void> = Promise.resolve();
  private readonly onAccusationArrestBound = (active: boolean): void => {
    this.accusationArrestActive = !!active;
  };
  private readonly onAccusationPhaseActiveBound = (active: boolean): void => {
    this.accusationPhaseActive = !!active;
  };
  private readonly refreshBuildingEntryArrowsBound = (): void => {
    this.refreshBuildingEntryArrows();
    this.refreshNpcNameplates();
  };
  private onCaseFrozenBound = (): void => {
    this.caseFrozen = true;
    this.pendingInteraction = null;
    this.freezePlayerMovement();
  };

  private readonly onPlayerControlsRestoredBound = (): void => {
    this.freezePlayerMovement();
    this.clearWalkSteer();
    this.pendingInteraction = null;
    this.resetIdleClock();
  };

  private readonly onUnstuckRequestBound = (): void => {
    this.teleportToZoneTransit();
  };

  private readonly onForcePoseEmit = (): void => {
    this.emitPlayerPoseIfDue(true);
  };

  private readonly onRebindSocket = (): void => {
    if (!this.peerHost) return;
    const sock = this.reg().socket;
    if (!sock) return;
    bindPeerSync(sock, this.peerHost, this.playerSkinsByUserId);
    rebindPlayerFollowSocket(sock);
    this.emitPlayerPoseIfDue(true);
  };
  private pendingInteraction: PendingInteraction | null = null;
  private peerHost!: PeerSyncHost;
  private playerSkinsByUserId = new Map<string, string>();
  private keyW?: Phaser.Input.Keyboard.Key;
  private keyA?: Phaser.Input.Keyboard.Key;
  private keyS?: Phaser.Input.Keyboard.Key;
  private keyD?: Phaser.Input.Keyboard.Key;

  constructor() {
    super("GameScene");
  }

  private reg(): Reg {
    return this.registry.get("ccc") as Reg;
  }

  private reducedMotion(): boolean {
    return (
      typeof globalThis.matchMedia === "function" &&
      globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  private isTextureReady(key: string): boolean {
    if (!this.textures.exists(key)) return false;
    const tex = this.textures.get(key);
    if (tex.key === "__MISSING") return false;
    const src = tex.getSourceImage() as HTMLImageElement | HTMLCanvasElement | null;
    if (!src) return false;
    const w = "naturalWidth" in src ? src.naturalWidth : src.width;
    if (!w || w < 2) return false;
    const source = tex.source?.[0] as { glTexture?: WebGLTexture | null } | undefined;
    if (
      this.sys.game.renderer.type === Phaser.WEBGL &&
      source &&
      source.glTexture === null
    ) {
      return false;
    }
    return true;
  }

  private buildingArtKey(buildingId: string): string {
    const key = `bld_art_${buildingId}`;
    return this.isTextureReady(key) ? key : "placeholder_bld";
  }

  private resolveNpcTextureKey(npcSkinId: string, dir: PlayableDir): string | null {
    const key = npcDirectionTextureKey(npcSkinId, dir);
    if (this.isTextureReady(key)) return key;
    const sKey = npcDirectionTextureKey(npcSkinId, "south");
    if (this.isTextureReady(sKey)) return sKey;
    return null;
  }

  private applyNpcTexture(
    vis: Phaser.GameObjects.Sprite,
    npcSkinId: string,
    dir: PlayableDir,
    opts?: { localPlayer?: boolean }
  ): void {
    const key = this.resolveNpcTextureKey(npcSkinId, dir);
    if (key) {
      setWorldCharacterTexture(vis, key);
      return;
    }
    this.queueMissingNpcTextures([npcSkinId], [dir, "south"]);
    setWorldCharacterPlaceholder(vis, { localPlayer: opts?.localPlayer });
  }

  private safePlayerSpawn(): { zone: ZoneId; tile_x: number; tile_y: number } {
    const { layerA } = this.reg();
    const s = layerA.player_spawn;
    const zone: ZoneId =
      s?.zone === "maple_heights" ? "maple_heights" : "downtown";
    const tx = s?.tile_x ?? 12;
    const ty = s?.tile_y ?? 12;
    const ok =
      Number.isFinite(tx) &&
      Number.isFinite(ty) &&
      tx >= LAYER_A_MARGIN_TILES &&
      tx < LAYER_A_GRID_W - LAYER_A_MARGIN_TILES &&
      ty >= LAYER_A_MARGIN_TILES &&
      ty < LAYER_A_GRID_H - LAYER_A_MARGIN_TILES;
    if (ok) return { zone, tile_x: Math.trunc(tx), tile_y: Math.trunc(ty) };
    return { zone: "downtown", tile_x: 12, tile_y: 12 };
  }

  /** Outdoor spawn on boot; uses resume neighborhood without a post-ready zone transit. */
  private initialOutdoorSpawn(): { zone: ZoneId; tile_x: number; tile_y: number } {
    const reg = this.reg();
    const resumeZone = reg.resumeZone;
    if (resumeZone) {
      const t = zoneTransitTile(resumeZone, reg.layerA);
      return { zone: resumeZone, tile_x: t.tile_x, tile_y: t.tile_y };
    }
    return this.safePlayerSpawn();
  }

  /** Unblocks session loader when a transit callback aborts after zone_transit_loading. */
  private failZoneTransit(failedZone: ZoneId): void {
    this.transitCooldown = 0;
    this.zoneTransitTimer?.remove();
    this.zoneTransitTimer = undefined;
    const cam = this.cameras.main;
    cam.fadeIn(180, 0, 0, 0);
    if (isFollowingLeader() && this.followCameraTarget) {
      this.resumeFollowCameraIfActive();
    } else if (this.playerRig) {
      cam.startFollow(
        this.playerRig,
        true,
        RUN_CAMERA_FOLLOW_LERP_X,
        RUN_CAMERA_FOLLOW_LERP_Y
      );
    }
    this.registry.events.emit("zone_transit_failed", failedZone);
  }

  create(): void {
    const bootFail = (phase: string, err: unknown): never => {
      const msg = err instanceof Error ? err.message : String(err);
      throw new Error(`GameScene.create [${phase}]: ${msg}`, { cause: err });
    };

    if (!this.scene.isActive("UIScene") && this.scene.get("UIScene")) {
      this.scene.launch("UIScene");
    }
    this.physics.world.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    const spawn = this.initialOutdoorSpawn();
    this.zone = spawn.zone;
    this.outdoorZone = spawn.zone;
    try {
      this.setupZone(this.zone);
    } catch (err) {
      bootFail("setupZone", err);
    }
    cleanAllBuildingTextures(this);
    this.clearBuildingTextureAlphaCache();
    try {
      this.spawnEntities();
    } catch (err) {
      bootFail("spawnEntities", err);
    }
    this.events.once(Phaser.Scenes.Events.POST_UPDATE, () => {
      if (!this.scene.isActive()) return;
      this.finalizeOutdoorBuildingArt();
    });
    try {
      this.createPlayerAt(spawn.tile_x, spawn.tile_y);
    } catch (err) {
      bootFail("player", err);
    }
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.setZoom(RUN_MAIN_CAMERA_ZOOM);
    this.cameras.main.roundPixels = true;
    this.interactHint = this.add
      .text(0, 0, "", {
        fontFamily: UI_FONT,
        fontSize: "17px",
        color: UI_TEXT.primary,
        fontStyle: "600",
        backgroundColor: "#121826ee",
        padding: { x: 12, y: 8 },
      })
      .setScrollFactor(0)
      .setDepth(1000)
      .setOrigin(0.5, 1)
      .setVisible(false);
    this.setupWalkAndInteractInput();
    this.scale.on("resize", this.layoutHud, this);
    this.layoutHud(this.scale.gameSize);
    this.events.on("shutdown", () => this.scale.off("resize", this.layoutHud, this));
    const reg = this.reg();
    for (const p of reg.players) {
      this.playerSkinsByUserId.set(p.userId, p.characterNpcId);
    }
    const labelForUserId = (uid: string): string => {
      const row = reg.players.find((p) => p.userId === uid);
      if (!row) return "Detective";
      return worldLabelForPlayer(row, reg.players);
    };
    this.peerHost = {
      scene: this,
      selfUserId: reg.userId,
      localZone: this.zone,
      localInteriorBuildingId: null,
      peers: new Map<string, PeerState>(),
      sessionPlayers: reg.players,
      labelForUserId,
      applyNpcTexture: (vis, skinId, dir) => this.applyNpcTexture(vis, skinId, dir),
      canTapPeer: () =>
        reg.playerCount > 1 &&
        !this.caseFrozen &&
        !isFollowingLeader() &&
        !this.accusationArrestActive &&
        !isNpcConversationOverlayOpen() &&
        !isNarrativeModalOpen() &&
        !isFollowConfirmOpen() &&
        !isZoneTransitConfirmOpen() &&
        !isPlayerInputFrozen(),
      peerTapBlockedReason: (peerUserId) => {
        if (reg.playerCount <= 1) return "solo";
        if (this.caseFrozen) return "caseFrozen";
        if (isFollowingLeader()) return "following";
        if (this.accusationArrestActive) return "arrest";
        const peer = this.peerHost?.peers.get(peerUserId);
        if (peer && !peer.rig.visible) return "peerHidden";
        return null;
      },
      onPeerTap: (userId) => onPeerTapped(userId),
      onPeerTapBlocked: (reason) =>
        this.showStatusMessage(peerTapBlockedMessage(reason), 5000),
    };
    bindPeerSync(reg.socket, this.peerHost, this.playerSkinsByUserId);
    seedPeersFromSession(this.peerHost, reg.players);

    this.cursors = this.input.keyboard?.createCursorKeys();
    this.keyE = this.input.keyboard?.addKey("E");
    const kb = this.input.keyboard;
    if (kb) {
      this.keyW = kb.addKey("W");
      this.keyA = kb.addKey("A");
      this.keyS = kb.addKey("S");
      this.keyD = kb.addKey("D");
    }
    const offsetSec = reg.timerElapsedOffsetSeconds ?? 0;
    this.elapsed = Math.max(0, offsetSec * 1000);
    if (reg.bootMeta?.alreadyResolved) {
      this.caseFrozen = true;
    }
    this.registry.events.emit("ccc_timer", Math.floor(this.elapsed / 1000));
    this.waypointArrow = createWaypointArrow(this);
    this.partnerWaypointArrow = createWaypointArrow(this, { stroke: 0x60a5fa });
    this.game.events.on(
      "ccc_npc_dialog_shown",
      (payload: { instanceId?: string }) => this.onNpcDialogShown(payload?.instanceId),
      this
    );
    this.game.events.on("ccc_npc_dialog_closed", this.onNpcDialogClosed, this);
    this.game.events.on("ccc_map_waypoint", this.onMapWaypointBound);
    this.game.events.on("ccc_partner_map_waypoint", this.onPartnerMapWaypointBound);
    this.game.events.on(
      "ccc_npc_dialog_focus",
      (p: { instanceId: string; wx: number; wy: number }) => {
        const run = [...this.npcSprites.values()].find(
          (r) => r.rig.getData("instance_id") === p.instanceId
        );
        if (run) {
          run.faceTowardWx = p.wx;
          run.faceTowardWy = p.wy;
        }
      }
    );
    this.onJournalOpen = (open: boolean) => {
      this.waypointArrow?.setHidden(!!open);
      this.partnerWaypointArrow?.setHidden(!!open);
      if (!open && this.pendingInteraction && !this.caseFrozen) {
        this.beginWalkTo(this.pendingInteraction.wx, this.pendingInteraction.wy);
      }
    };
    this.game.events.on("ccc_journal_open", this.onJournalOpen, this);
    this.game.events.on("ccc_player_controls_restored", this.onPlayerControlsRestoredBound);
    this.game.events.on("ccc_narrative_modal_closed", this.onPlayerControlsRestoredBound);
    this.game.events.on("ccc_unstuck_request", this.onUnstuckRequestBound);
    this.game.events.on("ccc_journal_sync", this.refreshBuildingEntryArrowsBound);
    this.resetIdleClock();
    this.game.events.on("ccc_case_frozen", this.onCaseFrozenBound);
    this.game.events.on("ccc_accusation_arrest", this.onAccusationArrestBound);
    this.game.events.on("ccc_accusation_phase_active", this.onAccusationPhaseActiveBound);
    this.registry.events.on("ccc_force_pose_emit", this.onForcePoseEmit);
    this.registry.events.on("ccc_rebind_socket", this.onRebindSocket);
    this.events.on("shutdown", () => {
      this.game.events.off("ccc_npc_dialog_shown", this.onNpcDialogShown, this);
      this.waypointArrow?.destroy();
      this.waypointArrow = undefined;
      this.partnerWaypointArrow?.destroy();
      this.partnerWaypointArrow = undefined;
      this.game.events.off("ccc_npc_dialog_closed", this.onNpcDialogClosed, this);
      this.game.events.off("ccc_map_waypoint", this.onMapWaypointBound);
      this.game.events.off("ccc_partner_map_waypoint", this.onPartnerMapWaypointBound);
      this.game.events.off("ccc_journal_open", this.onJournalOpen, this);
      this.game.events.off("ccc_player_controls_restored", this.onPlayerControlsRestoredBound);
      this.game.events.off("ccc_narrative_modal_closed", this.onPlayerControlsRestoredBound);
      this.game.events.off("ccc_unstuck_request", this.onUnstuckRequestBound);
      this.game.events.off("ccc_journal_sync", this.refreshBuildingEntryArrowsBound);
      destroyBuildingEntryArrows(this.buildingEntryArrows);
      this.setUnstuckVisible(false);
      this.activeDialogNpcId = null;
      this.setMapWaypoint(null);
      this.game.events.off("ccc_case_frozen", this.onCaseFrozenBound);
      this.game.events.off("ccc_accusation_arrest", this.onAccusationArrestBound);
      this.game.events.off("ccc_accusation_phase_active", this.onAccusationPhaseActiveBound);
      this.registry.events.off("ccc_force_pose_emit", this.onForcePoseEmit);
      this.registry.events.off("ccc_rebind_socket", this.onRebindSocket);
      if (this.peerHost) clearAllPeers(this.peerHost);
      if (this.zoneTransitTimer) {
        this.zoneTransitTimer.remove();
        this.zoneTransitTimer = undefined;
        this.registry.events.emit("zone_transit_failed", this.outdoorZone);
      }
      this.idleKeepaliveTimer?.remove();
      this.idleKeepaliveTimer = undefined;
      this.deferredAssetsStarted = false;
      this.npcArtRefreshHooked = false;
      this.zonesWithDeferredBuildings.clear();
    });
    this.idleKeepaliveTimer = this.time.addEvent({
      delay: PLAYER_IDLE_KEEPALIVE_MS,
      loop: true,
      callback: () => {
        if (this.caseFrozen || this.reg().playerCount <= 1) return;
        this.emitPlayerPoseIfDue(true);
      },
    });
    this.startDeferredAssetLoad();
    if (reg.resumeZone) {
      this.registry.events.emit("zone_changed", this.zone);
    }
    this.game.events.emit("world_ready");
    if (reg.playerCount > 1) {
      this.time.delayedCall(0, () => this.emitPlayerPoseIfDue(true));
    }
  }

  private onNpcDialogShown(instanceId?: string): void {
    this.activeDialogNpcId = instanceId ?? null;
    if (!instanceId) return;
    const run = [...this.npcSprites.values()].find(
      (r) => r.rig.getData("instance_id") === instanceId
    );
    if (run) {
      run.patrolPauseMs = 60_000;
      (run.rig.body as Phaser.Physics.Arcade.Body)?.setVelocity(0, 0);
    }
    if (this.playerRig) {
      this.game.events.emit("ccc_npc_dialog_focus", {
        instanceId,
        wx: this.playerRig.x,
        wy: this.playerRig.y,
      });
    }
  }

  private onNpcDialogClosed(): void {
    const id = this.activeDialogNpcId;
    this.activeDialogNpcId = null;
    if (id) {
      const run = [...this.npcSprites.values()].find(
        (r) => r.rig.getData("instance_id") === id
      );
      if (run) {
        run.patrolUnlockAtMs = this.time.now + 2000;
        run.faceTowardWx = undefined;
        run.faceTowardWy = undefined;
        (run.rig.body as Phaser.Physics.Arcade.Body)?.setVelocity(0, 0);
      }
    }
  }

  private setMapWaypoint(p: MapWaypointTarget | null): void {
    const next = p && isValidWorldWaypoint(p) ? p : null;
    this.mapWaypoint = next;
    this.waypointArrow?.setTarget(next ? { wx: next.wx, wy: next.wy } : null);
  }

  private tilePassableFn(): (tx: number, ty: number) => boolean {
    return (tx, ty) => {
      const wx = tx * TILE_SIZE + TILE_SIZE / 2;
      const wy = ty * TILE_SIZE + TILE_SIZE / 2;
      return this.isWorldPassable(wx, wy);
    };
  }

  private snapWalkWorldPoint(wx: number, wy: number): { x: number; y: number } | null {
    const tx = Math.floor(wx / TILE_SIZE);
    const ty = Math.floor(wy / TILE_SIZE);
    const hit = nearestWalkableTile(tx, ty, this.tilePassableFn(), 10);
    if (!hit) return null;
    return {
      x: hit.tx * TILE_SIZE + TILE_SIZE / 2,
      y: hit.ty * TILE_SIZE + TILE_SIZE / 2,
    };
  }

  private interactionBlocked(): boolean {
    return (
      this.caseFrozen ||
      this.accusationPhaseActive ||
      !shouldPhaserReadMovementKeys() ||
      isFollowingLeader()
    );
  }

  showStatusMessage(text: string, ms = 4000): void {
    this.game.events.emit("ccc_game_status", { text, ms });
  }

  getPeerRig(userId: string): Phaser.GameObjects.Container | null {
    return this.peerHost ? getPeerRig(this.peerHost, userId) : null;
  }

  getPeerFacing(userId: string): PlayableDir | null {
    return this.peerHost ? getPeerFacing(this.peerHost, userId) : null;
  }

  getPlayerBody(): Phaser.Physics.Arcade.Body | null {
    return (this.playerRig?.body as Phaser.Physics.Arcade.Body | undefined) ?? null;
  }

  getPlayerRigX(): number {
    return this.playerRig?.x ?? 0;
  }

  getPlayerRigY(): number {
    return this.playerRig?.y ?? 0;
  }

  getMinimapPlayerBlips(viewZone: ZoneId): MinimapPlayerBlip[] {
    const layerA = this.reg().layerA;
    const resolveIndoor = layerA
      ? (buildingId: string) => buildingMapCenter(layerA, buildingId)
      : undefined;
    const selfRig = this.playerRig;
    const self =
      selfRig &&
      (this.viewMode === "outdoor" ||
        (this.viewMode === "interior" && this.interiorBuildingId))
        ? {
            zone: this.outdoorZone,
            wx: selfRig.x,
            wy: selfRig.y,
            interiorBuildingId:
              this.viewMode === "interior" ? this.interiorBuildingId : null,
          }
        : undefined;
    return collectMinimapPlayerBlips({
      viewZone,
      self,
      peers: this.peerHost
        ? [...this.peerHost.peers.values()].map((p) => ({
            zone: p.zone,
            wx: p.lastPose?.x ?? p.rig.x,
            wy: p.lastPose?.y ?? p.rig.y,
            interiorBuildingId: p.interiorBuildingId,
          }))
        : undefined,
      resolveIndoorCenter: resolveIndoor,
    });
  }

  syncPlayerFacingFromVelocity(vx: number, vy: number): void {
    if (Math.hypot(vx, vy) > MIN_VEL_EPS) {
      this.lastPlayerDir = velocityToPlayableDir(vx, vy);
      this.applyNpcTexture(
        this.playerVis,
        this.reg().playerCharacterNpcId,
        this.lastPlayerDir
      );
    }
  }

  enterFollowMode(leaderUserId: string, _facing: PlayableDir, _slot: number): boolean {
    const rig = this.getPeerRig(leaderUserId);
    if (!rig || !this.playerRig) return false;
    this.followCameraTarget = rig;
    this.hasTarget = false;
    this.clearWalkSteer();
    this.freezePlayerMovement();
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.startFollow(
      rig,
      true,
      RUN_CAMERA_FOLLOW_LERP_X,
      RUN_CAMERA_FOLLOW_LERP_Y
    );
    return true;
  }

  exitFollowMode(): void {
    this.followCameraTarget = null;
    const cam = this.cameras.main;
    cam.stopFollow();
    if (this.playerRig) {
      cam.startFollow(
        this.playerRig,
        true,
        RUN_CAMERA_FOLLOW_LERP_X,
        RUN_CAMERA_FOLLOW_LERP_Y
      );
    }
  }

  async syncFollowLeaderWorld(
    zone: GameZoneId,
    buildingId: string | null
  ): Promise<void> {
    this.followWorldSyncQueue = this.followWorldSyncQueue.then(() =>
      this.runSyncFollowLeaderWorld(zone, buildingId)
    );
    return this.followWorldSyncQueue;
  }

  private async runSyncFollowLeaderWorld(
    zone: GameZoneId,
    buildingId: string | null
  ): Promise<void> {
    if (
      isFollowWorldStateSynced(
        {
          outdoorZone: this.outdoorZone,
          viewMode: this.viewMode,
          interiorBuildingId: this.interiorBuildingId,
        },
        zone,
        buildingId
      )
    ) {
      return;
    }
    closeZoneTransitConfirm();
    this.followWorldSyncing = true;
    try {
      if (this.outdoorZone !== zone && this.viewMode === "outdoor") {
        await this.syncFollowZoneChange(zone);
      } else if (this.outdoorZone !== zone && this.viewMode === "interior") {
        await this.syncFollowExitInterior();
        await this.syncFollowZoneChange(zone);
      }

      if (buildingId) {
        if (this.viewMode === "interior" && this.interiorBuildingId !== buildingId) {
          await this.syncFollowExitInterior();
        }
        if (this.interiorBuildingId !== buildingId) {
          await this.syncFollowEnterBuilding(buildingId);
        }
      } else if (this.viewMode === "interior") {
        await this.syncFollowExitInterior();
      }
    } finally {
      this.followWorldSyncing = false;
    }
  }

  private syncFollowZoneChange(next: ZoneId): Promise<void> {
    return new Promise((resolve) => {
      if (this.outdoorZone === next) {
        resolve();
        return;
      }
      this.performZoneChange(next);
      this.registry.events.once("zone_transit_done", () => resolve());
      this.registry.events.once("zone_transit_failed", () => resolve());
    });
  }

  private syncFollowExitInterior(): Promise<void> {
    return new Promise((resolve) => {
      if (this.viewMode !== "interior") {
        resolve();
        return;
      }
      this.performInteriorExit();
      this.registry.events.once("zone_transit_done", () => resolve());
      this.registry.events.once("zone_transit_failed", () => resolve());
    });
  }

  private emitLeaderWorldMirror(zone: ZoneId, buildingId: string | null): void {
    if (this.followWorldSyncing || isFollowingLeader()) return;
    emitLeaderMirror({ kind: "world", zone, buildingId });
  }

  private resumeFollowCameraIfActive(): void {
    if (!isFollowingLeader() || !this.followCameraTarget) return;
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.startFollow(
      this.followCameraTarget,
      true,
      RUN_CAMERA_FOLLOW_LERP_X,
      RUN_CAMERA_FOLLOW_LERP_Y
    );
  }

  private startCameraFollowAfterWorldTransition(
    useSoftLerp: boolean
  ): void {
    if (isFollowingLeader() && this.followCameraTarget) {
      this.resumeFollowCameraIfActive();
      return;
    }
    if (!this.playerRig) return;
    const cam = this.cameras.main;
    cam.startFollow(
      this.playerRig,
      true,
      useSoftLerp ? RUN_CAMERA_FOLLOW_LERP_SOFT : RUN_CAMERA_FOLLOW_LERP_X,
      useSoftLerp ? RUN_CAMERA_FOLLOW_LERP_SOFT : RUN_CAMERA_FOLLOW_LERP_Y
    );
  }

  private syncFollowEnterBuilding(buildingId: string): Promise<void> {
    return new Promise((resolve) => {
      const reg = this.reg();
      const placement = reg.layerA.building_placements?.find((p) => p.id === buildingId);
      const decor = placement?.decor === true;
      const plan = buildInteriorContentPlan(
        reg.layerA.seedId,
        reg.caseManifest,
        buildingId,
        reg.getJournal() as import("../lib/dialog-parser.js").CoherenceJournal,
        decor,
        { userId: reg.userId, playerCount: reg.playerCount }
      );
      const title = resolveBuildingDisplayName(reg.caseManifest, buildingId, { decor });
      this.enterBuildingInterior(buildingId, plan, title);
      this.registry.events.once("zone_transit_done", () => resolve());
      this.registry.events.once("zone_transit_failed", () => resolve());
    });
  }

  private freezePlayerMovement(): void {
    this.hasTarget = false;
    this.clearWalkSteer();
    (this.playerRig?.body as Phaser.Physics.Arcade.Body | undefined)?.setVelocity(0, 0);
  }

  private openNpcDialog(instanceId: string): void {
    this.pendingInteraction = null;
    this.registry.events.emit("open_dialog", instanceId);
  }

  private openBuilding(buildingId: string): void {
    this.pendingInteraction = null;
    if (this.viewMode === "interior") return;
    const spr = this.buildingSprites.get(buildingId);
    if (!spr) {
      this.registry.events.emit("open_building", buildingId);
      return;
    }
    this.saveExitStandFromSprite(spr);
    this.registry.events.emit("open_building", buildingId, { enterInterior: true });
  }

  private saveExitStandFromSprite(spr: Phaser.GameObjects.Sprite): void {
    const lot = this.buildingFootprintLot(spr);
    if (!lot) return;
    const buildingId = spr.getData("building_id") as string;
    const spineWx = spr.getData("spine_wx") as number;
    const spineWy = spr.getData("spine_wy") as number;
    const arrowState = buildingId ? this.buildingEntryArrows.get(buildingId) : undefined;
    let wx: number;
    let wy: number;
    if (arrowState) {
      wx = arrowState.spr.x;
      wy = arrowState.spr.y;
    } else if (this.mapGrid) {
      const stand = entryArrowStandWorldPoint(
        lot.left,
        lot.top,
        lot.span,
        spineWx,
        spineWy,
        this.mapGrid
      );
      wx = stand.wx;
      wy = stand.wy;
    } else {
      const stand = buildingInteractStandPoint({
        footLeft: lot.left,
        footTop: lot.top,
        footSpan: lot.span,
        spineWx,
        spineWy,
      });
      wx = stand.wx;
      wy = stand.wy;
    }
    this.savedExitStand = {
      wx,
      wy,
      outdoorZone: this.outdoorZone,
      footLeft: lot.left,
      footTop: lot.top,
      footSpan: lot.span,
      spineWx,
      spineWy,
    };
  }

  /** Called from main after permission check — transition into the interior. */
  enterBuildingInterior(
    buildingId: string,
    plan: InteriorContentPlan,
    displayName: string
  ): void {
    if (this.viewMode === "interior" || this.caseFrozen) return;
    this.interiorBuildingId = buildingId;
    this.interiorContentPlan = plan;
    this.interiorSpec = plan.spec;
    this.performInteriorEnter(buildingId, displayName);
  }

  private emitInteriorContext(buildingId: string | null): void {
    if (buildingId) {
      const reg = this.reg();
      const placement = reg.layerA.building_placements?.find((p) => p.id === buildingId);
      const decor = placement?.decor === true;
      const hudLabel = decor
        ? VACANT_PROPERTY_LABEL
        : resolveBuildingDisplayName(reg.caseManifest, buildingId, { decor });
      this.registry.events.emit("interior_context_changed", {
        mode: "interior",
        outdoorZone: this.outdoorZone,
        buildingId,
        buildingDisplayName: hudLabel,
      });
    } else {
      this.registry.events.emit("interior_context_changed", {
        mode: "outdoor",
        outdoorZone: this.outdoorZone,
      });
      this.registry.events.emit("zone_changed", this.outdoorZone);
    }
  }

  private performInteriorEnter(buildingId: string, displayName: string): void {
    if (!this.playerRig) return;
    if (this.transitCooldown > 0 && !this.followWorldSyncing) return;
    this.pendingInteraction = null;
    this.transitCooldown = 1400;
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.fadeOut(140, 0, 0, 0);
    this.registry.events.emit("zone_transit_loading", this.outdoorZone);
    this.zoneTransitTimer?.remove();
    this.zoneTransitTimer = this.time.delayedCall(150, () => {
      this.zoneTransitTimer = undefined;
      try {
        if (!this.playerRig || !this.interiorSpec) {
          this.failZoneTransit(this.outdoorZone);
          return;
        }
        this.viewMode = "interior";
        this.hasTarget = false;
        this.clearWalkSteer();
        (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
        this.destroyZoneEntities();
        for (const o of this.transitDecor) o.destroy();
        this.transitDecor = [];
        this.setupInterior(this.interiorSpec!, buildingId);
        this.spawnInteriorEntities(buildingId);
        const spawn = this.interiorSpec!.spawn_tile;
        const px = spawn.tile_x * TILE_SIZE + TILE_SIZE / 2;
        const py = spawn.tile_y * TILE_SIZE + TILE_SIZE / 2;
        this.playerRig.setPosition(px, py);
        this.lastSafePlayer.set(px, py);
        const iw = INTERIOR_GRID_W * TILE_SIZE;
        const ih = INTERIOR_GRID_H * TILE_SIZE;
        this.physics.world.setBounds(0, 0, iw, ih);
        cam.setBounds(0, 0, iw, ih);
        cam.centerOn(px, py);
        cam.fadeIn(220, 0, 0, 0);
        this.cameraLerpUntil = this.time.now + SPEED_BLEND_MS;
        this.cameraFollowRelaxed = isFollowingLeader();
        this.startCameraFollowAfterWorldTransition(true);
        this.emitInteriorContext(buildingId);
        this.reg().socket?.emit("game:zone_change", {
          sessionId: this.reg().sessionId,
          zone: this.outdoorZone,
          buildingId,
        });
        setPeerZoneVisibility(this.peerHost, this.outdoorZone, buildingId);
        this.emitLeaderWorldMirror(this.outdoorZone, buildingId);
        this.lastPoseEmitAtMs = 0;
        this.emitPlayerPoseIfDue(true);
        this.resetIdleClock();
        this.registry.events.emit("zone_transit_done");
      } catch (err) {
        console.error("interior enter failed", err);
        this.viewMode = "outdoor";
        this.interiorBuildingId = null;
        this.interiorSpec = null;
        this.transitCooldown = 0;
        cam.fadeIn(180, 0, 0, 0);
        this.restoreOutdoorWorldBounds();
        this.registry.events.emit("zone_transit_failed", this.outdoorZone);
      }
    });
  }

  private performInteriorExit(): void {
    if (this.viewMode !== "interior" || !this.playerRig) return;
    if (this.transitCooldown > 0 && !this.followWorldSyncing) return;
    const stand = this.savedExitStand;
    const outdoor = stand?.outdoorZone ?? this.outdoorZone;
    const buildingId = this.interiorBuildingId;
    this.pendingInteraction = null;
    this.transitCooldown = 1400;
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.fadeOut(140, 0, 0, 0);
    this.registry.events.emit("zone_transit_loading", outdoor);
    this.zoneTransitTimer?.remove();
    this.zoneTransitTimer = this.time.delayedCall(150, () => {
      this.zoneTransitTimer = undefined;
      try {
        if (!this.playerRig) {
          this.failZoneTransit(outdoor);
          return;
        }
        this.viewMode = "outdoor";
        this.interiorBuildingId = null;
        this.interiorSpec = null;
        this.interiorContentPlan = null;
        this.interiorMapGrid = null;
        this.interiorClueMarker?.destroy();
        this.interiorClueMarker = null;
        this.hasTarget = false;
        (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
        this.destroyZoneEntities();
        for (const o of this.transitDecor) o.destroy();
        this.transitDecor = [];
        this.zone = outdoor;
        this.outdoorZone = outdoor;
        this.restoreOutdoorWorldBounds();
        this.ensureZoneBuildingArt(outdoor);
        this.setupZone(outdoor);
        this.spawnEntities();
        this.finalizeOutdoorBuildingArt();
        let wx = stand?.wx ?? this.playerRig.x;
        let wy = stand?.wy ?? this.playerRig.y;
        const snapped = this.snapWalkWorldPoint(wx, wy);
        if (snapped) {
          wx = snapped.x;
          wy = snapped.y;
        }
        this.playerRig.setPosition(wx, wy);
        this.lastSafePlayer.set(wx, wy);
        this.clampPlayerToWalkable();
        cam.centerOn(this.playerRig.x, this.playerRig.y);
        cam.fadeIn(220, 0, 0, 0);
        this.cameraLerpUntil = this.time.now + SPEED_BLEND_MS;
        this.cameraFollowRelaxed = isFollowingLeader();
        this.startCameraFollowAfterWorldTransition(true);
        this.emitInteriorContext(null);
        this.reg().socket?.emit("game:zone_change", {
          sessionId: this.reg().sessionId,
          zone: outdoor,
          buildingId: null,
        });
        setPeerZoneVisibility(this.peerHost, outdoor, null);
        if (this.peerHost) {
          const followers = getLeaderFollowerUserIds();
          if (followers.length > 0) {
            snapFollowersToLeaderExit(this.peerHost, followers, {
              zone: outdoor,
              wx,
              wy,
              buildingId: null,
            });
          }
        }
        this.emitLeaderWorldMirror(outdoor, null);
        this.lastPoseEmitAtMs = 0;
        this.emitPlayerPoseIfDue(true);
        this.resetIdleClock();
        this.registry.events.emit("zone_transit_done");
        if (buildingId) {
          this.registry.events.emit("building_interior_exited", buildingId);
        }
      } catch (err) {
        console.error("interior exit failed", err);
        this.transitCooldown = 0;
        cam.fadeIn(180, 0, 0, 0);
        this.registry.events.emit("zone_transit_failed", outdoor);
      }
    });
  }

  private restoreOutdoorWorldBounds(): void {
    this.physics.world.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
  }

  private setupInterior(spec: BuildingInteriorSpec, buildingId: string): void {
    if (this.ground) this.ground.destroy();
    this.interiorMapGrid = new InteriorMapGrid(
      spec,
      this,
      buildingId,
      this.outdoorZone === "downtown"
    );
    this.ground = this.interiorMapGrid.render();
    this.createInteriorExitArrow(spec);
  }

  private spawnInteriorEntities(buildingId: string): void {
    const plan = this.interiorContentPlan;
    if (!plan) return;
    const { caseManifest } = this.reg();
    if (plan.showClueMarker) {
      this.interiorClueMarker = spawnClueMarker(this, plan.spec, buildingId, () =>
        this.queueInteriorClueInteract()
      );
    }
    if (plan.interiorNpcInstanceId) {
      const n = caseManifest.npcs.find((x) => x.instance_id === plan.interiorNpcInstanceId);
      const nt = interiorNpcTile(plan.spec);
      if (n && nt) {
        const { wx, wy } = tileToWorldCenter(nt.tile_x, nt.tile_y);
        const skin = n.npc_id;
        const rig = this.add.container(wx, wy);
        const vis = this.add.sprite(0, 0, "placeholder_npc");
        this.applyNpcTexture(vis, skin, "south");
        const nameplate = createWorldNameplateText(
          this,
          npcWorldNameplateLabel(caseManifest, n.instance_id, this.reg().getJournal()),
          { npc: true }
        );
        rig.add(vis);
        rig.add(nameplate);
        syncNameplateAboveSprite(nameplate, vis);
        rig.setDepth(5);
        rig.setData("instance_id", n.instance_id);
        rig.setData("type", "npc");
        rig.setData("name", n.name);
        this.bindNpcPointer(rig);
        const patrolPickSeq = 0;
        const patrolRng = this.npcPatrolRand(n.instance_id, patrolPickSeq);
        const firstPatrol =
          pickInteriorPatrolTarget(plan.spec, wx, wy, {
            filler: n.role === "filler",
            rng: patrolRng,
          }) ?? { x: wx, y: wy };
        const aggressive = n.role === "filler" && n.filler_profile?.wander === "aggressive";
        const pauseMin = aggressive
          ? FILLER_AGGRESSIVE_PATROL_PAUSE_MIN_MS
          : NPC_PATROL_PAUSE_MIN_MS;
        const pauseMax = aggressive
          ? FILLER_AGGRESSIVE_PATROL_PAUSE_MAX_MS
          : NPC_PATROL_PAUSE_MAX_MS;
        this.npcSprites.set(n.instance_id, {
          rig,
          vis,
          nameplate,
          patrolTargetX: firstPatrol.x,
          patrolTargetY: firstPatrol.y,
          patrolPauseMs: randIntBetween(patrolRng, pauseMin, pauseMax),
          patrolPickSeq: patrolPickSeq + 1,
          patrolPauseMinMs: aggressive
            ? FILLER_AGGRESSIVE_PATROL_PAUSE_MIN_MS
            : NPC_PATROL_PAUSE_MIN_MS,
          patrolPauseMaxMs: aggressive
            ? FILLER_AGGRESSIVE_PATROL_PAUSE_MAX_MS
            : NPC_PATROL_PAUSE_MAX_MS,
          patrolUnlockAtMs: 0,
          npcSkinId: skin,
          lastFacingDir: "south",
          interiorPatrol: true,
          wanderStyle:
            n.filler_profile?.wander === "aggressive"
              ? "aggressive"
              : n.filler_profile?.wander === "stationary"
                ? "stationary"
                : n.role === "filler"
                  ? "default"
                  : undefined,
          congregationGroupId: n.filler_profile?.congregation_group_id,
          bobPhase: Math.random() * Math.PI * 2,
        });
      }
    }
    void buildingId;
  }

  private createInteriorExitArrow(spec: BuildingInteriorSpec): void {
    const chevronKey = "trans_arrow_chevron_exit_red";
    ensureTransitChevronTexture(this, chevronKey, INTERIOR_EXIT_CHEVRON_PALETTE);
    const { wx: cx, wy: cy } = interiorExitArrowWorldPoint(spec);
    const rotation = interiorExitArrowRotation(spec.exit_wall);
    const spr = this.add.image(cx, cy, chevronKey);
    spr.setRotation(rotation);
    spr.setDepth(6);
    spr.setAlpha(0.92);
    spr.setInteractive(
      new Phaser.Geom.Circle(0, 0, TILE_SIZE * 1.35),
      Phaser.Geom.Circle.Contains
    );
    spr.input!.cursor = "pointer";
    spr.setData("type", "interior_exit");
    spr.on("pointerdown", (
      _pointer: Phaser.Input.Pointer,
      _lx: number,
      _ly: number,
      event: Phaser.Types.Input.EventData
    ) => {
      event.stopPropagation();
      if (!this.pointerInPlayfield(_pointer) || this.interactionBlocked()) return;
      this.queueInteriorExitInteract();
    });
    this.tweens.add({
      targets: spr,
      alpha: { from: 0.65, to: 1 },
      duration: 600,
      yoyo: true,
      repeat: -1,
    });
    this.transitDecor.push(spr);
  }

  private playerNearInteriorExit(px: number, py: number): boolean {
    if (!this.interiorSpec) return false;
    const { wx, wy } = interiorExitTileCenter(this.interiorSpec);
    return (
      Phaser.Math.Distance.Between(px, py, wx, wy) <=
      TILE_SIZE * INTERIOR_EXIT_INTERACT_TILES
    );
  }

  private stopPlayerForInteract(): void {
    this.hasTarget = false;
    this.clearWalkSteer();
    (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
  }

  private worldInteractRangeCtx(): WorldInteractRangeCtx {
    return {
      zone: this.zone,
      layerA: this.reg().layerA,
      interiorSpec: this.interiorSpec,
      transitInteractTiles: TRANSIT_INTERACT_TILES,
      npcInteractRadiusPx: this.npcInteractRadiusPx(),
      entryArrowInteractMaxDistPx: entryArrowInteractMaxDistPx(),
      npcRigPosition: (instanceId) => {
        const run = [...this.npcSprites.values()].find(
          (r) => r.rig.getData("instance_id") === instanceId
        );
        return run ? { x: run.rig.x, y: run.rig.y } : null;
      },
    };
  }

  private isPendingInRange(pending: PendingInteraction): boolean {
    return pendingInteractInRange(
      this.playerRig.x,
      this.playerRig.y,
      pending,
      this.worldInteractRangeCtx()
    );
  }

  private pendingTargetStillExists(pending: PendingInteraction): boolean {
    switch (pending.kind) {
      case "npc":
        return [...this.npcSprites.values()].some(
          (r) => r.rig.getData("instance_id") === pending.instanceId
        );
      case "building_entry":
        return this.buildingEntryArrows.has(pending.buildingId);
      case "zone_transit":
        return this.viewMode === "outdoor";
      case "interior_exit":
        return this.viewMode === "interior" && this.interiorSpec != null;
      case "interior_clue":
        return (
          this.viewMode === "interior" &&
          this.interiorSpec != null &&
          this.interiorContentPlan?.showClueMarker === true
        );
      default:
        return false;
    }
  }

  private queueWorldInteract(
    pending: PendingInteraction,
    inRange: () => boolean
  ): void {
    if (inRange()) {
      this.fulfillPendingInteraction(pending);
      return;
    }
    this.pendingInteraction = pending;
    this.beginWalkTo(pending.wx, pending.wy);
  }

  private fulfillPendingInteraction(pending: PendingInteraction): void {
    this.stopPlayerForInteract();
    switch (pending.kind) {
      case "npc":
        this.openNpcDialog(pending.instanceId);
        break;
      case "building_entry":
        this.openBuildingViaEntryArrow(pending.buildingId);
        break;
      case "zone_transit":
        this.promptZoneTransit();
        break;
      case "interior_exit":
        this.promptInteriorExit();
        break;
      case "interior_clue":
        this.registry.events.emit("interior_clue_interact", pending.buildingId);
        break;
      default:
        break;
    }
  }

  private queueNpcInteract(instanceId: string, rig: Phaser.GameObjects.Container): void {
    this.queueWorldInteract(
      { kind: "npc", instanceId, wx: rig.x, wy: rig.y },
      () => this.npcWithinInteractPx(this.playerRig.x, this.playerRig.y, rig)
    );
  }

  private queueBuildingEntryInteract(buildingId: string): void {
    const state = this.buildingEntryArrows.get(buildingId);
    if (!state) return;
    const px = this.playerRig.x;
    const py = this.playerRig.y;
    this.queueWorldInteract(
      {
        kind: "building_entry",
        buildingId,
        wx: state.spr.x,
        wy: state.spr.y,
      },
      () => findBuildingEntryArrowAt(this.buildingEntryArrows, px, py) === buildingId
    );
  }

  private queueZoneTransitInteract(): void {
    const { wx, wy } = zoneTransitArrowWorldPoint(this.zone, this.reg().layerA);
    const px = this.playerRig.x;
    const py = this.playerRig.y;
    this.queueWorldInteract(
      { kind: "zone_transit", wx, wy },
      () => this.playerNearTransit(px, py)
    );
  }

  private queueInteriorExitInteract(): void {
    const spec = this.interiorSpec;
    if (!spec) return;
    const { wx, wy } = interiorExitArrowWorldPoint(spec);
    const px = this.playerRig.x;
    const py = this.playerRig.y;
    this.queueWorldInteract(
      { kind: "interior_exit", wx, wy },
      () => this.playerNearInteriorExit(px, py)
    );
  }

  private queueInteriorClueInteract(): void {
    const spec = this.interiorSpec;
    const buildingId = this.interiorBuildingId;
    if (!spec?.content_tile || !buildingId) return;
    const point = clueMarkerWorldPoint(spec);
    if (!point) return;
    const px = this.playerRig.x;
    const py = this.playerRig.y;
    this.queueWorldInteract(
      { kind: "interior_clue", buildingId, wx: point.wx, wy: point.wy },
      () => playerNearClueMarker(px, py, spec)
    );
  }

  private tryFulfillPendingInteraction(): void {
    if (!this.pendingInteraction || this.caseFrozen) {
      return;
    }
    if (!shouldPhaserReadMovementKeys()) return;
    const pending = this.pendingInteraction;
    if (!this.pendingTargetStillExists(pending)) {
      this.pendingInteraction = null;
      return;
    }
    if (!this.isPendingInRange(pending)) return;
    this.pendingInteraction = null;
    this.fulfillPendingInteraction(pending);
  }

  private openBuildingViaEntryArrow(buildingId: string): void {
    const state = this.buildingEntryArrows.get(buildingId);
    if (state?.permitted) this.openBuilding(buildingId);
    else this.registry.events.emit("open_building", buildingId);
  }

  private layoutHud(size: Phaser.Structs.Size): void {
    layoutInteractHint(this.interactHint, size.width, size.height);
    layoutClueToastIfVisible(this);
  }

  private clearWalkSteer(): void {
    this.walkPointerId = null;
    this.walkSteerActive = false;
  }

  private pointerInPlayfield(pointer: Phaser.Input.Pointer): boolean {
    return pointer.active && pointer.y >= 88;
  }

  private setupWalkAndInteractInput(): void {
    this.input.setTopOnly(true);
    if (this.walkZone) this.walkZone.destroy();
    this.walkZone = this.add
      .zone(WORLD_WIDTH * 0.5, WORLD_HEIGHT * 0.5, WORLD_WIDTH, WORLD_HEIGHT)
      .setScrollFactor(1)
      .setDepth(-50);
    this.walkZone.setInteractive();
    this.walkZone.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerInPlayfield(pointer) || !shouldPhaserReadMovementKeys()) return;
      const { x, y } = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      if (this.peerHost) {
        const peerId = findPeerUserIdAtWorldPoint(this.peerHost, x, y);
        if (peerId) {
          if (this.peerHost.canTapPeer?.()) {
            this.peerHost.onPeerTap?.(peerId);
          } else {
            const reason = this.peerHost.peerTapBlockedReason?.(peerId);
            if (reason) this.peerHost.onPeerTapBlocked?.(reason);
          }
          return;
        }
      }
      this.pendingInteraction = null;
      this.beginWalkTo(x, y, pointer);
    });

    const onPointerMove = (pointer: Phaser.Input.Pointer) => {
      this.tickWalkSteer(pointer);
    };
    const onPointerUp = (pointer: Phaser.Input.Pointer) => {
      if (this.walkPointerId === pointer.id) this.clearWalkSteer();
    };
    this.input.on("pointermove", onPointerMove);
    this.input.on("pointerup", onPointerUp);
    this.input.on("pointerupoutside", onPointerUp);
    this.events.on("shutdown", () => {
      this.input.off("pointermove", onPointerMove);
      this.input.off("pointerup", onPointerUp);
      this.input.off("pointerupoutside", onPointerUp);
    });
  }

  private beginWalkTo(
    wx: number,
    wy: number,
    pointer?: Phaser.Input.Pointer
  ): void {
    const snapped = this.snapWalkWorldPoint(wx, wy);
    if (!snapped) return;
    this.target.set(snapped.x, snapped.y);
    this.hasTarget = true;
    this.walkPointerId = pointer?.id ?? null;
    this.walkDownAtMs = this.time.now;
    this.walkSteerActive = false;
    this.walkSteerDragPx = 0;
    if (pointer) {
      this.walkSteerDownX = pointer.x;
      this.walkSteerDownY = pointer.y;
    }
  }

  private walkSteerDownX = 0;
  private walkSteerDownY = 0;

  private tickWalkSteer(pointer: Phaser.Input.Pointer): void {
    if (this.walkPointerId == null || pointer.id !== this.walkPointerId) return;
    if (!pointer.isDown) {
      this.clearWalkSteer();
      return;
    }
    this.walkSteerDragPx = Math.hypot(
      pointer.x - this.walkSteerDownX,
      pointer.y - this.walkSteerDownY
    );
    if (!this.pointerInPlayfield(pointer)) return;
    if (
      this.walkSteerDragPx > 8 ||
      this.time.now - this.walkDownAtMs >= PLAYFIELD_WALK_STEER_HOLD_MS
    ) {
      this.walkSteerActive = true;
    }
    if (!this.walkSteerActive) return;
    const { x, y } = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const snapped = this.snapWalkWorldPoint(x, y);
    if (!snapped) return;
    this.target.set(snapped.x, snapped.y);
    this.hasTarget = true;
  }

  private bindNpcPointer(rig: Phaser.GameObjects.Container): void {
    rig.setInteractive(
      new Phaser.Geom.Circle(0, 0, NPC_TAP_HIT_RADIUS_PX),
      Phaser.Geom.Circle.Contains
    );
    rig.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerInPlayfield(pointer) || this.interactionBlocked()) return;
      this.hasTarget = false;
      this.clearWalkSteer();
      (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      this.queueNpcInteract(rig.getData("instance_id") as string, rig);
    });
  }

  private spineIndexNear(strip: LayerAWalkStrip, tx: number, ty: number): number {
    const sx = strip.spine_tx;
    const sy = strip.spine_ty;
    if (!sx || !sy || sx.length < 1) return 0;
    let best = 0;
    let bd = Infinity;
    for (let i = 0; i < sx.length; i++) {
      const d = Math.abs(sx[i]! - tx) + Math.abs(sy[i]! - ty);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    return best;
  }

  private createTransitArrows(): void {
    for (const o of this.transitDecor) o.destroy();
    this.transitDecor = [];
    const { layerA } = this.reg();
    const { wx: cx, wy: cy } = zoneTransitArrowWorldPoint(this.zone, layerA);
    const rotation = zoneTransitArrowRotation(this.zone);

    const chevronKey = "trans_arrow_chevron_base";
    ensureTransitChevronTexture(this, chevronKey, ZONE_TRANSIT_CHEVRON_PALETTE);

    const spr = this.add.image(cx, cy, chevronKey);
    spr.setRotation(rotation);
    spr.setDepth(6);
    spr.setAlpha(0.92);
    spr.setInteractive(
      new Phaser.Geom.Circle(0, 0, TILE_SIZE * 1.35),
      Phaser.Geom.Circle.Contains
    );
    spr.input!.cursor = "pointer";
    spr.on("pointerdown", (
      _pointer: Phaser.Input.Pointer,
      _lx: number,
      _ly: number,
      event: Phaser.Types.Input.EventData
    ) => {
      event.stopPropagation();
      if (!this.pointerInPlayfield(_pointer) || this.interactionBlocked()) return;
      this.queueZoneTransitInteract();
    });
    this.tweens.add({
      targets: spr,
      alpha: { from: 0.65, to: 1 },
      duration: 600,
      yoyo: true,
      repeat: -1,
    });
    this.transitDecor.push(spr);
  }

  private setupZone(zone: ZoneId): void {
    this.zone = zone;
    this.outdoorZone = zone;
    this.viewMode = "outdoor";
    this.interiorMapGrid = null;
    if (this.ground) this.ground.destroy();
    const { layerA } = this.reg();
    this.mapGrid = new MapGrid(zone, this, layerA);
    this.ground = this.mapGrid.render();
    this.createTransitArrows();
    this.spawnAccentDynamicVfx();
  }

  private destroyZoneEntities(): void {
    this.clearAccentVfxObjects();
    destroyBuildingEntryArrows(this.buildingEntryArrows);
    for (const spr of this.buildingSprites.values()) {
      spr.destroy();
    }
    for (const run of this.npcSprites.values()) run.rig.destroy();
    this.buildingSprites.clear();
    this.npcSprites.clear();
  }

  private ensureAccentTexturesExist(): void {
    if (!this.textures.exists("glow_particle_white")) {
      const canvas = document.createElement("canvas");
      canvas.width = 16;
      canvas.height = 16;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const grad = ctx.createRadialGradient(8, 8, 0, 8, 8, 8);
        grad.addColorStop(0, "rgba(255, 255, 255, 1)");
        grad.addColorStop(0.3, "rgba(255, 255, 255, 0.8)");
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 16, 16);
        this.textures.addCanvas("glow_particle_white", canvas);
      }
    }

    if (!this.textures.exists("smoke_particle")) {
      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
        grad.addColorStop(0, "rgba(220, 220, 220, 0.4)");
        grad.addColorStop(0.4, "rgba(200, 200, 200, 0.15)");
        grad.addColorStop(1, "rgba(200, 200, 200, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 32, 32);
        this.textures.addCanvas("smoke_particle", canvas);
      }
    }

    if (!this.textures.exists("neon_light_glow")) {
      const canvas = document.createElement("canvas");
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
        grad.addColorStop(0, "rgba(255, 255, 255, 0.5)");
        grad.addColorStop(0.4, "rgba(255, 255, 255, 0.15)");
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 64, 64);
        this.textures.addCanvas("neon_light_glow", canvas);
      }
    }
  }

  private spawnAccentDynamicVfx(): void {
    this.clearAccentVfxObjects();
    const { layerA } = this.reg();
    if (!layerA || !layerA.accents) return;

    this.ensureAccentTexturesExist();

    for (const acc of layerA.accents) {
      if (acc.zone !== this.zone) continue;

      const wx = acc.tile_x * TILE_SIZE + TILE_SIZE / 2;
      const wy = acc.tile_y * TILE_SIZE + TILE_SIZE / 2;

      if (acc.type === "steam_vent") {
        const emitter = this.add.particles(wx, wy - 4, "smoke_particle", {
          lifespan: { min: 1000, max: 1500 },
          speedY: { min: -25, max: -12 },
          speedX: { min: -6, max: 6 },
          scale: { start: 0.4, end: 1.5 },
          alpha: { start: 0.5, end: 0 },
          frequency: 180,
          gravityY: -5,
          blendMode: "NORMAL",
        });
        emitter.setDepth(4);
        this.accentVfxObjects.push(emitter);
      } else if (acc.type === "mystic_sparkle") {
        const colorVal = Phaser.Display.Color.HexStringToColor(acc.color ?? "#ffd700");
        const emitter = this.add.particles(wx, wy, "glow_particle_white", {
          lifespan: { min: 1500, max: 2200 },
          speedY: { min: -12, max: -4 },
          speedX: { min: -8, max: 8 },
          scale: { start: 0.25, end: 0.05 },
          alpha: { start: 1, end: 0 },
          frequency: 250,
          tint: colorVal.color,
          blendMode: "ADD",
        });
        emitter.setDepth(5);
        this.accentVfxObjects.push(emitter);
      } else if (acc.type === "neon_drain") {
        const colorStr = acc.color ?? "#00f0ff";
        const colorVal = Phaser.Display.Color.HexStringToColor(colorStr);
        const glow = this.add.image(wx, wy, "neon_light_glow");
        glow.setDepth(1);
        glow.setTint(colorVal.color);
        glow.setAlpha(0.28);
        glow.setBlendMode(Phaser.BlendModes.ADD);

        this.tweens.add({
          targets: glow,
          alpha: { from: 0.18, to: 0.38 },
          scale: { from: 0.9, to: 1.05 },
          duration: 1800 + Math.random() * 600,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        });

        this.accentVfxObjects.push(glow);
      }
    }
  }

  private clearAccentVfxObjects(): void {
    for (const obj of this.accentVfxObjects) {
      obj.destroy();
    }
    this.accentVfxObjects = [];
  }

  private refreshBuildingEntryArrows(): void {
    if (this.viewMode !== "outdoor" || !this.mapGrid) return;
    const reg = this.reg();
    syncBuildingEntryArrows(
      {
        scene: this,
        mapGrid: this.mapGrid,
        buildingSprites: this.buildingSprites,
        getManifest: () => reg.caseManifest,
        getJournal: () =>
          (this.registry.get("coherenceJournal") as CoherenceJournal | undefined) ?? {},
        investigationOpts: () => ({
          userId: reg.userId,
          playerCount: reg.playerCount,
          manifest: reg.caseManifest,
        }),
        interactionBlocked: () => this.interactionBlocked(),
        onGreenTap: (bid) => this.queueBuildingEntryInteract(bid),
        onRedTap: (bid) => this.queueBuildingEntryInteract(bid),
      },
      this.buildingEntryArrows
    );
  }

  /** Flip façade so the front faces the walk strip (toward spine). */
  private buildingFlipTowardRoad(
    p: { tile_x: number; tile_y: number },
    strip: LayerAWalkStrip
  ): boolean {
    const sx = strip.spine_tx;
    const sy = strip.spine_ty;
    if (!sx || !sy || sx.length < 1) return false;
    const fcx = p.tile_x + BUILDING_FOOTPRINT_TILES * 0.5;
    const fcy = p.tile_y + BUILDING_FOOTPRINT_TILES * 0.5;
    let bi = 0;
    let bd = Infinity;
    for (let i = 0; i < sx.length; i++) {
      const d = Math.abs(fcx - sx[i]!) + Math.abs(fcy - sy[i]!);
      if (d < bd) {
        bd = d;
        bi = i;
      }
    }
    const i0 = Math.max(0, bi - 1);
    const i1 = Math.min(sx.length - 1, bi + 1);
    const tdx = sx[i1]! - sx[i0]!;
    const tdy = sy[i1]! - sy[i0]!;
    const dx = fcx - sx[bi]!;
    const dy = fcy - sy[bi]!;
    const cross = tdx * dy - tdy * dx;
    return cross < 0;
  }

  private spawnBuildingPlacement(
    p: { id: string; zone: string; tile_x: number; tile_y: number; decor?: boolean },
    b: { is_crime_scene?: boolean } | undefined
  ): void {
    if (typeof p.id !== "string" || !p.id.trim()) return;
    const footPx = BUILDING_FOOTPRINT_TILES * TILE_SIZE;
    const footLeft = p.tile_x * TILE_SIZE;
    const footTop = p.tile_y * TILE_SIZE;
    let cx = footLeft + footPx / 2;
    let cy = footTop + footPx - TILE_SIZE * 0.35;
    const { layerA } = this.reg();
    const strip = resolveWalkStrip(layerA, p.zone === "maple_heights" ? "maple_heights" : "downtown");
    const fcx = p.tile_x + BUILDING_FOOTPRINT_TILES * 0.5;
    const fcy = p.tile_y + BUILDING_FOOTPRINT_TILES * 0.5;
    const snap = nearestSpineVertex(strip, fcx, fcy);
    const spineWx = snap.tx * TILE_SIZE + TILE_SIZE / 2;
    const spineWy = snap.ty * TILE_SIZE + TILE_SIZE / 2;
    const corridorHalfPx = (strip.corridor_half_width ?? 7) * TILE_SIZE;
    const fromSpineX = cx - spineWx;
    const fromSpineY = cy - spineWy;
    const fromLen = Math.hypot(fromSpineX, fromSpineY) || 1;
    /** Flush to corridor outer edge — ignore sidewalk band. */
    const targetFromSpinePx = Math.max(corridorHalfPx - TILE_SIZE * 0.75, TILE_SIZE * 1.85);
    cx = spineWx + (fromSpineX / fromLen) * targetFromSpinePx;
    cy = spineWy + (fromSpineY / fromLen) * targetFromSpinePx;

    const texKey = this.buildingArtKey(p.id);
    const spr = this.add.sprite(cx, cy, texKey);
    spr.setScrollFactor(1, 1);
    spr.setFlipX(this.buildingFlipTowardRoad(p, strip));
    /** SE (larger tile sum) draws in front when façades overlap along the diagonal row. */
    spr.setDepth(3 + (p.tile_x + p.tile_y) * 0.02);
    spr.setData("building_id", p.id);
    spr.setData("decor", p.decor === true);
    spr.setData("type", "building");
    spr.setData("foot_left", footLeft);
    spr.setData("foot_top", footTop);
    spr.setData("foot_span", footPx);
    spr.setData("spine_wx", spineWx);
    spr.setData("spine_wy", spineWy);
    const tint: BuildingArtTint =
      texKey === "placeholder_bld" ? "placeholder" : b?.is_crime_scene ? "crime_scene" : "none";
    setBuildingArtTexture(spr, texKey, tint);
    this.buildingSprites.set(p.id, spr);
  }

  private spawnEntities(): void {
    const { caseManifest, layerA } = this.reg();
    this.npcPatrolByInstance.clear();
    for (const seg of layerA.npc_patrols ?? []) {
      this.npcPatrolByInstance.set(seg.instance_id, seg);
    }
    const placements = layerA.building_placements ?? [];
    for (const p of placements) {
      if (p.zone !== this.zone) continue;
      const b = caseManifest.buildings.find((x) => x.building_id === p.id);
      this.spawnBuildingPlacement(p, b);
    }
    for (const p of layerA.npc_placements ?? []) {
      if (p.zone !== this.zone) continue;
      const n = caseManifest.npcs.find((x) => x.instance_id === p.id);
      if (n?.position_near_building?.trim()) continue;
      const skin = n?.npc_id ?? "bartender_m";
      const rig = this.add.container(
        p.tile_x * TILE_SIZE + TILE_SIZE / 2,
        p.tile_y * TILE_SIZE + TILE_SIZE / 2
      );
      const vis = this.add.sprite(0, 0, "placeholder_npc");
      this.applyNpcTexture(vis, skin, "south");
      const npcLabel = npcWorldNameplateLabel(
        caseManifest,
        p.id,
        this.reg().getJournal()
      );
      const nameplate = createWorldNameplateText(this, npcLabel, { npc: true });
      rig.add(vis);
      rig.add(nameplate);
      syncNameplateAboveSprite(nameplate, vis);
      rig.setDepth(5);
      rig.setData("instance_id", p.id);
      rig.setData("type", "npc");
      if (n) rig.setData("name", n.name);
      this.bindNpcPointer(rig);
      const px = p.tile_x * TILE_SIZE + TILE_SIZE / 2;
      const py = p.tile_y * TILE_SIZE + TILE_SIZE / 2;
      const seg = this.npcPatrolByInstance.get(p.id);
      const wanderStyle = seg?.wander_style;
      const aggressive = wanderStyle === "aggressive";
      const patrolPickSeq = 0;
      const patrolRng = this.npcPatrolRand(p.id, patrolPickSeq);
      const pauseMin = aggressive
        ? FILLER_AGGRESSIVE_PATROL_PAUSE_MIN_MS
        : NPC_PATROL_PAUSE_MIN_MS;
      const pauseMax = aggressive
        ? FILLER_AGGRESSIVE_PATROL_PAUSE_MAX_MS
        : NPC_PATROL_PAUSE_MAX_MS;
      this.npcSprites.set(p.id, {
        rig,
        vis,
        nameplate,
        patrolTargetX: px,
        patrolTargetY: py,
        patrolPauseMs: randIntBetween(patrolRng, pauseMin, pauseMax),
        patrolPickSeq: patrolPickSeq + 1,
        patrolPauseMinMs: aggressive
          ? FILLER_AGGRESSIVE_PATROL_PAUSE_MIN_MS
          : NPC_PATROL_PAUSE_MIN_MS,
        patrolPauseMaxMs: aggressive
          ? FILLER_AGGRESSIVE_PATROL_PAUSE_MAX_MS
          : NPC_PATROL_PAUSE_MAX_MS,
        patrolUnlockAtMs: 0,
        npcSkinId: skin,
        lastFacingDir: "south",
        wanderStyle,
        congregationGroupId: seg?.congregation_group_id,
        bobPhase: Math.random() * Math.PI * 2,
      });
    }
    this.deoverlapNpcSpritesInZone();
    this.refreshBuildingEntryArrows();
  }

  /** Spread NPCs that share a spawn tile (legacy layouts) along the patrol spine. */
  private deoverlapNpcSpritesInZone(): void {
    const groups = new Map<string, NpcRuntime[]>();
    for (const run of this.npcSprites.values()) {
      const tx = Math.round((run.rig.x - TILE_SIZE / 2) / TILE_SIZE);
      const ty = Math.round((run.rig.y - TILE_SIZE / 2) / TILE_SIZE);
      const k = `${tx},${ty}`;
      const list = groups.get(k) ?? [];
      list.push(run);
      groups.set(k, list);
    }
    for (const runs of groups.values()) {
      if (runs.length < 2) continue;
      for (let i = 0; i < runs.length; i++) {
        const run = runs[i]!;
        const id = run.rig.getData("instance_id") as string;
        const seg = this.npcPatrolByInstance.get(id);
        if (!seg) continue;
        const strip = resolveWalkStrip(
          this.reg().layerA,
          seg.zone === "maple_heights" ? "maple_heights" : "downtown"
        );
        const sx = strip.spine_tx;
        const sy = strip.spine_ty;
        if (!sx || !sy || sx.length < 2) continue;
        const base = this.spineIndexNear(strip, seg.tile_x0, seg.tile_y0);
        const step = i - Math.floor(runs.length / 2);
        const idx = Math.max(0, Math.min(sx.length - 1, base + step * 2));
        const wx = sx[idx]! * TILE_SIZE + TILE_SIZE / 2;
        const wy = sy[idx]! * TILE_SIZE + TILE_SIZE / 2;
        run.rig.setPosition(wx, wy);
        run.patrolTargetX = wx;
        run.patrolTargetY = wy;
      }
    }
  }

  private createPlayerAt(tileX: number, tileY: number): void {
    const px = tileX * TILE_SIZE + TILE_SIZE / 2;
    const py = tileY * TILE_SIZE + TILE_SIZE / 2;
    const { playerCharacterNpcId } = this.reg();
    this.playerRig = this.add.container(px, py);
    this.playerVis = this.add.sprite(0, 0, "placeholder_npc");
    this.applyNpcTexture(this.playerVis, playerCharacterNpcId, "south", {
      localPlayer: true,
    });
    const reg = this.reg();
    const localLabel = worldLabelForPlayer(
      { userId: reg.userId, displayName: reg.playerName },
      reg.players
    );
    this.playerNameplate = createWorldNameplateText(this, localLabel, {
      localViewer: true,
    });
    this.playerRig.add(this.playerVis);
    this.playerRig.add(this.playerNameplate);
    syncNameplateAboveSprite(this.playerNameplate, this.playerVis);
    this.playerRig.setDepth(10);
    this.physics.add.existing(this.playerRig, false);
    const body = this.playerRig.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(true);
    body.setSize(
      Math.round(WORLD_CHARACTER_DISPLAY_PX * 0.42),
      Math.round(WORLD_CHARACTER_DISPLAY_PX * 0.35)
    );
    body.setOffset(
      -Math.round(WORLD_CHARACTER_DISPLAY_PX * 0.21),
      -Math.round(WORLD_CHARACTER_DISPLAY_PX * 0.2)
    );
    this.lastSafePlayer.set(px, py);
    this.resetIdleClock();
    this.cameras.main.startFollow(
      this.playerRig,
      true,
      RUN_CAMERA_FOLLOW_LERP_X,
      RUN_CAMERA_FOLLOW_LERP_Y
    );
  }

  private resetIdleClock(): void {
    this.lastMovementAtMs = this.time?.now ?? 0;
    if (this.playerRig) {
      this.lastIdleSampleX = this.playerRig.x;
      this.lastIdleSampleY = this.playerRig.y;
    }
    this.setUnstuckVisible(false);
  }

  private setUnstuckVisible(visible: boolean): void {
    if (this.unstuckOffered === visible) return;
    this.unstuckOffered = visible;
    this.game.events.emit("ccc_unstuck_visible", visible);
  }

  private canOfferUnstuck(): boolean {
    if (!this.playerRig) return false;
    if (isFollowingLeader()) return false;
    if (this.caseFrozen || this.accusationArrestActive) return false;
    if (this.transitCooldown > 0) return false;
    if (isPlayerInputFrozen()) return false;
    return true;
  }

  private updateUnstuckOffer(): void {
    if (!this.playerRig) return;
    const body = this.playerRig.body as Phaser.Physics.Arcade.Body;
    const intentMoving =
      this.hasTarget ||
      this.walkSteerActive ||
      Math.hypot(body.velocity.x, body.velocity.y) > 1;
    const result = updateUnstuckMotion({
      px: this.playerRig.x,
      py: this.playerRig.y,
      sampleX: this.lastIdleSampleX,
      sampleY: this.lastIdleSampleY,
      lastMovementAtMs: this.lastMovementAtMs,
      velX: body.velocity.x,
      velY: body.velocity.y,
      nowMs: this.time.now,
      canOffer: this.canOfferUnstuck(),
      intentMoving,
    });
    this.lastIdleSampleX = result.sampleX;
    this.lastIdleSampleY = result.sampleY;
    this.lastMovementAtMs = result.lastMovementAtMs;
    this.setUnstuckVisible(result.show);
  }

  private teleportToZoneTransit(): void {
    if (!this.playerRig || !this.mapGrid) return;
    this.pendingInteraction = null;
    this.hasTarget = false;
    this.clearWalkSteer();
    (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);

    const { layerA } = this.reg();
    let { wx, wy } = zoneTransitArrowWorldPoint(this.zone, layerA);
    const snapped = this.snapWalkWorldPoint(wx, wy);
    if (snapped) {
      wx = snapped.x;
      wy = snapped.y;
    }
    this.playerRig.setPosition(wx, wy);
    this.clampPlayerToWalkable();
    this.lastSafePlayer.set(this.playerRig.x, this.playerRig.y);
    this.cameras.main.centerOn(this.playerRig.x, this.playerRig.y);
    this.resetIdleClock();
    this.emitPlayerPoseIfDue(true);
  }

  private readKeyboardDir(): { x: number; y: number } {
    if (!shouldPhaserReadMovementKeys()) return { x: 0, y: 0 };
    let x = 0;
    let y = 0;
    if (this.cursors?.left.isDown || this.keyA?.isDown) x -= 1;
    if (this.cursors?.right.isDown || this.keyD?.isDown) x += 1;
    if (this.cursors?.up.isDown || this.keyW?.isDown) y -= 1;
    if (this.cursors?.down.isDown || this.keyS?.isDown) y += 1;
    return { x, y };
  }

  private buildingFootprintLot(
    spr: Phaser.GameObjects.Sprite
  ): { left: number; top: number; span: number } | undefined {
    const left = spr.getData("foot_left") as number | undefined;
    const top = spr.getData("foot_top") as number | undefined;
    const span = spr.getData("foot_span") as number | undefined;
    if (left === undefined || top === undefined || span === undefined) return undefined;
    return { left, top, span };
  }

  private clearBuildingTextureAlphaCache(): void {
    this.buildingTextureAlphaRows.clear();
  }

  private buildingTextureAlphaRow(key: string): Uint8ClampedArray | undefined {
    const cached = this.buildingTextureAlphaRows.get(key);
    if (cached) return cached.alpha;
    if (!this.textures.exists(key)) return undefined;
    const source = this.textures.get(key).getSourceImage();
    if (!(source instanceof HTMLCanvasElement)) return undefined;
    const ctx = source.getContext("2d");
    if (!ctx) return undefined;
    const w = source.width;
    const h = source.height;
    const img = ctx.getImageData(0, 0, w, h);
    const alpha = new Uint8ClampedArray(w * h);
    for (let i = 0, p = 0; i < w * h; i++, p += 4) {
      alpha[i] = img.data[p + 3]!;
    }
    this.buildingTextureAlphaRows.set(key, { width: w, height: h, alpha });
    return alpha;
  }

  private buildingOpaqueSampleFn(
    spr: Phaser.GameObjects.Sprite
  ): BuildingOpaqueAlphaSample | undefined {
    const key = spr.texture.key;
    if (key === "placeholder_bld") return undefined;
    const alphaRow = this.buildingTextureAlphaRow(key);
    if (!alphaRow) return undefined;
    const cached = this.buildingTextureAlphaRows.get(key)!;
    const frame = spr.frame;
    return (u: number, v: number) => {
      const px = Math.floor(
        frame.cutX + Math.max(0, Math.min(1, u)) * Math.max(0, frame.cutWidth - 1)
      );
      const py = Math.floor(
        frame.cutY + Math.max(0, Math.min(1, v)) * Math.max(0, frame.cutHeight - 1)
      );
      if (px < 0 || py < 0 || px >= cached.width || py >= cached.height) return 0;
      return alphaRow[py * cached.width + px] ?? 0;
    };
  }

  private worldPointHitsBuildingOpaque(
    spr: Phaser.GameObjects.Sprite,
    wx: number,
    wy: number,
    forMovement = false
  ): boolean {
    const bid = spr.getData("building_id") as string | undefined;
    if (!bid) return false;
    const anchor = buildingAnchorFromSprite(spr);
    return worldPointHitsBuildingOpaqueMask({
      assetId: bid,
      anchorWorldX: anchor.anchorWorldX,
      anchorWorldY: anchor.anchorWorldY,
      displayWidth: spr.displayWidth,
      displayHeight: spr.displayHeight,
      wx,
      wy,
      sampleAlpha: forMovement ? undefined : this.buildingOpaqueSampleFn(spr),
      useTextureAlpha: !forMovement,
      alphaThreshold: BUILDING_FOOTPRINT_ALPHA_TOLERANCE,
      footprintLot: this.buildingFootprintLot(spr),
    });
  }

  /** Movement: mask / footprint only — walk-strip tiles ignore sprite overhang separately. */
  private isBlockedByBuildingOpaqueForMovement(wx: number, wy: number): boolean {
    for (const spr of this.buildingSprites.values()) {
      if (spr.getData("decor") === true) continue;
      if (this.worldPointHitsBuildingOpaque(spr, wx, wy, true)) return true;
    }
    return false;
  }

  private isBlockedByBuildingOpaque(wx: number, wy: number): boolean {
    for (const spr of this.buildingSprites.values()) {
      if (spr.getData("decor") === true) continue;
      if (this.worldPointHitsBuildingOpaque(spr, wx, wy, false)) return true;
    }
    return false;
  }

  /** True when (tx,ty) is walk-strip or within a tile of it — avoids huge off-road transparent pads. */
  private isNearWalkCorridor(tx: number, ty: number, radius = 2): boolean {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (this.mapGrid.isWalkableTile(tx + dx, ty + dy)) return true;
      }
    }
    return false;
  }

  /** Transparent façade on the road-facing half only — avoids side snags into sprite bounds. */
  private isOnBuildingTransparentPad(wx: number, wy: number): boolean {
    if (this.isBlockedByBuildingOpaqueForMovement(wx, wy)) return false;
    const tx = Math.floor(wx / TILE_SIZE);
    const ty = Math.floor(wy / TILE_SIZE);
    if (!this.isNearWalkCorridor(tx, ty, 1)) return false;
    for (const spr of this.buildingSprites.values()) {
      if (spr.getData("decor") === true) continue;
      const spineWx = spr.getData("spine_wx") as number;
      const spineWy = spr.getData("spine_wy") as number;
      const anchor = buildingAnchorFromSprite(spr);
      const toSpineX = spineWx - anchor.anchorWorldX;
      const toSpineY = spineWy - anchor.anchorWorldY;
      const relX = wx - anchor.anchorWorldX;
      const relY = wy - anchor.anchorWorldY;
      if (relX * toSpineX + relY * toSpineY <= 0) continue;
      const bid = spr.getData("building_id") as string;
      const maskBox = bid
        ? buildingMaskWorldBounds(
            bid,
            anchor.anchorWorldX,
            anchor.anchorWorldY,
            spr.displayWidth,
            spr.displayHeight
          )
        : null;
      if (maskBox) {
        if (wx >= maskBox.left && wx <= maskBox.right && wy >= maskBox.top && wy <= maskBox.bottom) {
          return true;
        }
        continue;
      }
      if (spr.getBounds().contains(wx, wy)) return true;
    }
    return false;
  }

  /** One-tile nudge toward the zone spine when slide-back fails near a building row. */
  private nudgePlayerTowardSpine(wx: number, wy: number): { wx: number; wy: number } | null {
    const { layerA } = this.reg();
    const strip = resolveWalkStrip(layerA, this.zone === "maple_heights" ? "maple_heights" : "downtown");
    const tx = Math.floor(wx / TILE_SIZE);
    const ty = Math.floor(wy / TILE_SIZE);
    const snap = nearestSpineVertex(strip, tx, ty);
    const spineWx = snap.tx * TILE_SIZE + TILE_SIZE / 2;
    const spineWy = snap.ty * TILE_SIZE + TILE_SIZE / 2;
    const dx = spineWx - wx;
    const dy = spineWy - wy;
    const len = Math.hypot(dx, dy) || 1;
    const step = TILE_SIZE * 0.92;
    const nx = wx + (dx / len) * step;
    const ny = wy + (dy / len) * step;
    if (this.isWorldPassable(nx, ny)) return { wx: nx, wy: ny };
    return null;
  }

  /** Slide along the road spine when wedged beside a façade on a walk-strip tile. */
  private nudgePlayerAlongSpine(wx: number, wy: number): { wx: number; wy: number } | null {
    const { layerA } = this.reg();
    const strip = resolveWalkStrip(layerA, this.zone === "maple_heights" ? "maple_heights" : "downtown");
    const sx = strip.spine_tx;
    const sy = strip.spine_ty;
    if (!sx || !sy || sx.length < 2) return null;
    const tx = Math.floor(wx / TILE_SIZE);
    const ty = Math.floor(wy / TILE_SIZE);
    const i = Math.max(0, Math.min(sx.length - 2, nearestSpineIndex(strip, tx, ty)));
    const tdx = sx[i + 1]! - sx[i]!;
    const tdy = sy[i + 1]! - sy[i]!;
    const tLen = Math.hypot(tdx, tdy) || 1;
    const step = TILE_SIZE * 0.55;
    for (const sign of [-1, 1] as const) {
      const nx = wx + (tdx / tLen) * step * sign;
      const ny = wy + (tdy / tLen) * step * sign;
      if (this.isWorldPassable(nx, ny)) return { wx: nx, wy: ny };
    }
    return null;
  }

  private isWorldPassable(wx: number, wy: number): boolean {
    const tx = Math.floor(wx / TILE_SIZE);
    const ty = Math.floor(wy / TILE_SIZE);
    if (this.viewMode === "interior" && this.interiorMapGrid) {
      return this.interiorMapGrid.isWalkableTile(tx, ty);
    }
    if (tx < 0 || ty < 0 || tx >= TILES_W || ty >= TILES_H) return false;
    if (this.mapGrid.isWalkableTile(tx, ty)) return true;
    if (this.isBlockedByBuildingOpaqueForMovement(wx, wy)) return false;
    if (this.isOnBuildingTransparentPad(wx, wy)) return true;
    return false;
  }

  private clampPlayerToWalkable(): void {
    const px = this.playerRig.x;
    const py = this.playerRig.y;
    if (this.isWorldPassable(px, py)) {
      this.lastSafePlayer.set(px, py);
      return;
    }
    const slid = slideToNearestPassable(
      px,
      py,
      this.lastSafePlayer.x,
      this.lastSafePlayer.y,
      TILE_SIZE,
      this.tilePassableFn(),
      8,
      (wx, wy) => this.isWorldPassable(wx, wy)
    );
    this.playerRig.setPosition(slid.wx, slid.wy);
    const body = this.playerRig.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, 0);
    if (slid.stuck) {
      const stx = Math.floor(slid.wx / TILE_SIZE);
      const sty = Math.floor(slid.wy / TILE_SIZE);
      const nearCorridor = this.isNearWalkCorridor(stx, sty, 1);
      const nudged =
        (nearCorridor ? this.nudgePlayerAlongSpine(slid.wx, slid.wy) : null) ??
        this.nudgePlayerTowardSpine(slid.wx, slid.wy);
      if (nudged) {
        this.playerRig.setPosition(nudged.wx, nudged.wy);
        this.lastSafePlayer.set(nudged.wx, nudged.wy);
        return;
      }
      this.hasTarget = false;
      this.clearWalkSteer();
      return;
    }
    this.lastSafePlayer.set(slid.wx, slid.wy);
    if (this.hasTarget && !this.isWorldPassable(this.target.x, this.target.y)) {
      this.hasTarget = false;
    }
  }

  private updatePointerTargetVelocity(): void {
    if (!this.hasTarget) {
      (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      return;
    }
    const dx = this.target.x - this.playerRig.x;
    const dy = this.target.y - this.playerRig.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 10) {
      this.hasTarget = false;
      (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      return;
    }
    (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(
      (dx / dist) * RUN_PLAYER_SPEED_PX_S,
      (dy / dist) * RUN_PLAYER_SPEED_PX_S
    );
  }

  /** Throttled pixel pose broadcast for co-op (skipped in solo). */
  emitPlayerPoseIfDue(force = false): void {
    if (this.caseFrozen || this.reg().playerCount <= 1) return;
    const sock = this.reg().socket;
    if (!sock?.connected || !this.playerRig) return;

    const x = this.playerRig.x;
    const y = this.playerRig.y;
    const now = this.time.now;
    const moved = Math.hypot(x - this.lastPoseEmitX, y - this.lastPoseEmitY);
    const facingChanged = this.lastPlayerDir !== this.lastPoseEmitFacing;
    const zoneChanged = this.outdoorZone !== this.lastPoseEmitZone;
    const intervalOk = now - this.lastPoseEmitAtMs >= PLAYER_POSE_MIN_INTERVAL_MS;

    if (
      !force &&
      !zoneChanged &&
      !facingChanged &&
      (!intervalOk || moved < PLAYER_POSE_MIN_MOVE_PX)
    ) {
      return;
    }

    this.poseSeq += 1;
    this.lastPoseEmitAtMs = now;
    this.lastPoseEmitX = x;
    this.lastPoseEmitY = y;
    this.lastPoseEmitFacing = this.lastPlayerDir;
    this.lastPoseEmitZone = this.outdoorZone;

    const buildingId =
      this.viewMode === "interior" && this.interiorBuildingId
        ? this.interiorBuildingId
        : null;

    sock.emit("game:player_move", {
      sessionId: this.reg().sessionId,
      x,
      y,
      zone: this.outdoorZone,
      buildingId,
      facing: this.lastPlayerDir,
      seq: this.poseSeq,
      clientSentAtMs: Date.now(),
    });
  }

  private updateFacingAndBob(delta: number): void {
    const body = this.playerRig.body as Phaser.Physics.Arcade.Body;
    const vx = body.velocity.x;
    const vy = body.velocity.y;
    const sp = Math.hypot(vx, vy);
    if (sp > MIN_VEL_EPS) {
      this.lastPlayerDir = velocityToPlayableDir(vx, vy);
    }
    this.applyNpcTexture(this.playerVis, this.reg().playerCharacterNpcId, this.lastPlayerDir, {
      localPlayer: true,
    });
    const rm = this.reducedMotion();
    const moving = sp > MIN_VEL_EPS;
    this.playerVis.y = worldCharacterWalkBobY(this.bobPhase, moving, rm);
    this.bobPhase += delta * WORLD_CHARACTER_BOB_SPEED;
    syncNameplateAboveSprite(this.playerNameplate, this.playerVis);
  }

  private npcPatrolRand(instanceId: string, step: number): () => number {
    return npcPatrolRng(this.reg().layerA.seedId, instanceId, step);
  }

  private nextPatrolPauseMs(run: NpcRuntime, instanceId: string): number {
    const rng = this.npcPatrolRand(instanceId, run.patrolPickSeq++);
    return randIntBetween(rng, run.patrolPauseMinMs, run.patrolPauseMaxMs);
  }

  private pickInteriorNpcPatrolTarget(run: NpcRuntime): { x: number; y: number } | null {
    if (!this.interiorSpec) return null;
    const iid = String(run.rig.getData("instance_id") ?? "");
    const rng = this.npcPatrolRand(iid, run.patrolPickSeq++);
    return pickInteriorPatrolTarget(this.interiorSpec, run.rig.x, run.rig.y, {
      filler: run.wanderStyle != null,
      rng,
    });
  }

  private pickNpcPatrolTarget(
    run: NpcRuntime,
    seg: LayerANpcPatrol
  ): { x: number; y: number } | null {
    const iid = String(run.rig.getData("instance_id") ?? "");
    const rng = this.npcPatrolRand(iid, run.patrolPickSeq++);
    const x0 = seg.tile_x0 * TILE_SIZE + TILE_SIZE / 2;
    const y0 = seg.tile_y0 * TILE_SIZE + TILE_SIZE / 2;
    const x1 = seg.tile_x1 * TILE_SIZE + TILE_SIZE / 2;
    const y1 = seg.tile_y1 * TILE_SIZE + TILE_SIZE / 2;
    for (let attempt = 0; attempt < 10; attempt++) {
      const tx = x0 + rng() * (x1 - x0);
      const ty = y0 + rng() * (y1 - y0);
      if (this.isWorldPassable(tx, ty)) {
        return { x: tx, y: ty };
      }
    }
    return this.isWorldPassable(run.rig.x, run.rig.y) ? { x: run.rig.x, y: run.rig.y } : null;
  }

  private updateNpcMotion(delta: number): void {
    for (const [id, run] of this.npcSprites) {
      if (run.patrolUnlockAtMs > 0 && this.time.now < run.patrolUnlockAtMs) {
        (run.rig.body as Phaser.Physics.Arcade.Body)?.setVelocity(0, 0);
        continue;
      }
      if (run.patrolUnlockAtMs > 0 && this.time.now >= run.patrolUnlockAtMs) {
        run.patrolUnlockAtMs = 0;
      }
      if (id === this.activeDialogNpcId) {
        (run.rig.body as Phaser.Physics.Arcade.Body)?.setVelocity(0, 0);
        if (
          run.faceTowardWx != null &&
          run.faceTowardWy != null &&
          this.playerRig
        ) {
          const dx = run.faceTowardWx - run.rig.x;
          const dy = run.faceTowardWy - run.rig.y;
          const dir =
            Math.hypot(dx, dy) > 0.1 ? velocityToPlayableDir(dx, dy) : run.lastFacingDir;
          run.lastFacingDir = dir;
          this.applyNpcTexture(run.vis, run.npcSkinId, dir);
        }
        continue;
      }
      const seg = this.npcPatrolByInstance.get(id);
      let vx = 0;
      let vy = 0;

      const patrolInterior =
        this.viewMode === "interior" && run.interiorPatrol && this.interiorSpec;

      if (patrolInterior) {
        if (run.patrolPauseMs > 0) {
          run.patrolPauseMs -= delta;
        } else {
          const dx = run.patrolTargetX - run.rig.x;
          const dy = run.patrolTargetY - run.rig.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 10) {
            run.patrolPauseMs = this.nextPatrolPauseMs(run, id);
            const next = this.pickInteriorNpcPatrolTarget(run);
            if (next) {
              run.patrolTargetX = next.x;
              run.patrolTargetY = next.y;
            }
          } else {
            const step = RUN_NPC_PATROL_SPEED_PX_S * (delta / 1000);
            const nx = run.rig.x + (dx / dist) * step;
            const ny = run.rig.y + (dy / dist) * step;
            if (this.isWorldPassable(nx, ny)) {
              run.rig.setPosition(nx, ny);
              vx = (dx / dist) * RUN_NPC_PATROL_SPEED_PX_S;
              vy = (dy / dist) * RUN_NPC_PATROL_SPEED_PX_S;
            } else {
              run.patrolPauseMs = this.nextPatrolPauseMs(run, id);
              const next = this.pickInteriorNpcPatrolTarget(run);
              if (next) {
                run.patrolTargetX = next.x;
                run.patrolTargetY = next.y;
              }
            }
          }
        }
      } else if (seg && seg.zone === this.zone) {
        const patrolSpeed =
          run.wanderStyle === "aggressive"
            ? FILLER_AGGRESSIVE_PATROL_SPEED_PX_S
            : RUN_NPC_PATROL_SPEED_PX_S;
        if (run.patrolPauseMs > 0) {
          run.patrolPauseMs -= delta;
        } else if (run.wanderStyle === "stationary") {
          vx = 0;
          vy = 0;
        } else {
          const dx = run.patrolTargetX - run.rig.x;
          const dy = run.patrolTargetY - run.rig.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 10) {
            run.patrolPauseMs = this.nextPatrolPauseMs(run, id);
            const next = this.pickNpcPatrolTarget(run, seg);
            if (next) {
              run.patrolTargetX = next.x;
              run.patrolTargetY = next.y;
            }
          } else {
            const step = patrolSpeed * (delta / 1000);
            const nx = run.rig.x + (dx / dist) * step;
            const ny = run.rig.y + (dy / dist) * step;
            if (this.isWorldPassable(nx, ny)) {
              run.rig.setPosition(nx, ny);
              vx = (dx / dist) * patrolSpeed;
              vy = (dy / dist) * patrolSpeed;
            } else {
              run.patrolPauseMs = this.nextPatrolPauseMs(run, id);
              const next = this.pickNpcPatrolTarget(run, seg);
              if (next) {
                run.patrolTargetX = next.x;
                run.patrolTargetY = next.y;
              }
            }
          }
        }

        for (const [oid, other] of this.npcSprites) {
          if (oid === id) continue;
          const sameGroup =
            run.congregationGroupId != null &&
            run.congregationGroupId === other.congregationGroupId;
          const sepPx = sameGroup ? FILLER_CONGREGATION_SEPARATION_PX : NPC_SEPARATION_PX;
          const d = Phaser.Math.Distance.Between(
            run.rig.x,
            run.rig.y,
            other.rig.x,
            other.rig.y
          );
          if (d < sepPx && d > 0.1) {
            run.patrolPauseMs = Math.max(run.patrolPauseMs, 400);
            const next = this.pickNpcPatrolTarget(run, seg);
            if (next) {
              run.patrolTargetX = next.x;
              run.patrolTargetY = next.y;
            }
            break;
          }
        }
      }

      const dir: PlayableDir =
        Math.hypot(vx, vy) > MIN_VEL_EPS ? velocityToPlayableDir(vx, vy) : run.lastFacingDir;
      run.lastFacingDir = dir;
      this.applyNpcTexture(run.vis, run.npcSkinId, dir);
      const rm = this.reducedMotion();
      const moving = Math.hypot(vx, vy) > MIN_VEL_EPS;
      run.vis.y = worldCharacterWalkBobY(run.bobPhase, moving, rm);
      if (moving) {
        run.bobPhase += delta * WORLD_CHARACTER_BOB_SPEED;
      }
    }
  }

  private promptZoneTransit(): void {
    if (this.viewMode !== "outdoor") return;
    if (this.interactionBlocked()) return;
    if (this.transitCooldown > 0) return;
    if (isZoneTransitConfirmOpen()) return;
    const next = zoneTransitDestination(this.zone);
    openZoneTransitConfirm({
      title: "Travel",
      message: zoneTransitConfirmMessage(this.reg().caseManifest, this.zone),
      onConfirm: () => this.performZoneChange(next),
    });
  }

  private promptInteriorExit(): void {
    if (this.viewMode !== "interior") return;
    if (this.interactionBlocked()) return;
    if (this.transitCooldown > 0) return;
    if (isZoneTransitConfirmOpen()) return;
    openZoneTransitConfirm({
      title: "Exit",
      message: interiorExitConfirmMessage(this.reg().caseManifest, this.outdoorZone),
      onConfirm: () => this.performInteriorExit(),
    });
  }

  private performZoneChange(next: ZoneId): void {
    if (this.transitCooldown > 0 && !this.followWorldSyncing) return;
    if (!this.playerRig || !this.mapGrid) return;
    this.pendingInteraction = null;
    this.transitCooldown = 1400;
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.fadeOut(140, 0, 0, 0);
    this.registry.events.emit("zone_transit_loading", next);
    this.zoneTransitTimer?.remove();
    this.zoneTransitTimer = this.time.delayedCall(150, () => {
      this.zoneTransitTimer = undefined;
      try {
        if (!this.playerRig) {
          this.failZoneTransit(next);
          return;
        }
        this.hasTarget = false;
        (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
        this.zone = next;
        this.outdoorZone = next;
        this.viewMode = "outdoor";
        this.ensureZoneBuildingArt(next);
        this.destroyZoneEntities();
        this.setupZone(next);
        this.queueMissingNpcTextures(this.npcSkinIdsForZone(this.reg(), next), ["south"]);
        this.spawnEntities();
        this.finalizeOutdoorBuildingArt();
        const spawn = zoneTransitTile(next, this.reg().layerA);
        const px = spawn.tile_x * TILE_SIZE + TILE_SIZE / 2;
        const py = spawn.tile_y * TILE_SIZE + TILE_SIZE / 2;
        this.playerRig.setPosition(px, py);
        this.lastSafePlayer.set(px, py);
        cam.centerOn(px, py);
        cam.fadeIn(220, 0, 0, 0);
        this.cameraLerpUntil = this.time.now + SPEED_BLEND_MS;
        this.cameraFollowRelaxed = isFollowingLeader();
        this.startCameraFollowAfterWorldTransition(true);
        this.registry.events.emit("zone_changed", next);
        setPeerZoneVisibility(this.peerHost, next, null);
        this.reg().socket?.emit("game:zone_change", {
          sessionId: this.reg().sessionId,
          zone: next,
        });
        this.emitLeaderWorldMirror(next, null);
        this.lastPoseEmitAtMs = 0;
        this.emitPlayerPoseIfDue(true);
        this.resetIdleClock();
        this.registry.events.emit("zone_transit_done");
      } catch (err) {
        console.error("zone transit failed", err);
        this.transitCooldown = 0;
        cam.fadeIn(180, 0, 0, 0);
        if (isFollowingLeader() && this.followCameraTarget) {
          this.resumeFollowCameraIfActive();
        } else if (this.playerRig) {
          cam.startFollow(
            this.playerRig,
            true,
            RUN_CAMERA_FOLLOW_LERP_X,
            RUN_CAMERA_FOLLOW_LERP_Y
          );
        }
        this.registry.events.emit("zone_transit_failed", next);
      }
    });
  }

  update(_time: number, delta: number): void {
    if (!this.playerRig || !this.mapGrid) return;
    this.transitCooldown = Math.max(0, this.transitCooldown - delta);
    const soloCase = this.reg().playerCount <= 1;
    const pauseTimerWhileReading = soloCase && isPlayerInputFrozen();
    const pauseTimerDuringArrest = !soloCase && this.accusationArrestActive;
    if (!pauseTimerWhileReading && !pauseTimerDuringArrest && !this.caseFrozen) {
      this.elapsed += delta;
    }
    const elapsedSec = Math.floor(this.elapsed / 1000);
    this.registry.events.emit("ccc_timer", elapsedSec);

    if (this.caseFrozen || !shouldPhaserReadMovementKeys()) {
      this.freezePlayerMovement();
    }

    const cam = this.cameras.main;
    if (!this.cameraFollowRelaxed && this.time.now >= this.cameraLerpUntil) {
      this.cameraFollowRelaxed = true;
      if (isFollowingLeader() && this.followCameraTarget) {
        this.resumeFollowCameraIfActive();
      } else {
        cam.startFollow(
          this.playerRig,
          true,
          RUN_CAMERA_FOLLOW_LERP_X,
          RUN_CAMERA_FOLLOW_LERP_Y
        );
      }
    }
    if (isFollowingLeader()) {
      tickFollowMovement(this, delta);
    } else {
      const k = shouldPhaserReadMovementKeys() ? this.readKeyboardDir() : { x: 0, y: 0 };
      if (k.x !== 0 || k.y !== 0) {
        this.hasTarget = false;
        const len = Math.hypot(k.x, k.y);
        (this.playerRig.body as Phaser.Physics.Arcade.Body).setVelocity(
          (k.x / len) * RUN_PLAYER_SPEED_PX_S,
          (k.y / len) * RUN_PLAYER_SPEED_PX_S
        );
      } else {
        this.updatePointerTargetVelocity();
      }
    }

    this.clampPlayerToWalkable();
    this.updateUnstuckOffer();
    this.tryFulfillPendingInteraction();
    this.updateFacingAndBob(delta);
    this.updateNpcMotion(delta);
    this.emitPlayerPoseIfDue();
    if (!this.caseFrozen && this.peerHost && this.reg().playerCount > 1) {
      tickPeerInterpolation(this.peerHost, delta);
    }
    this.updateInteract();

    const camView = {
      x: cam.scrollX,
      y: cam.scrollY,
      width: cam.width,
      height: cam.height,
    };
    this.waypointArrow?.update(camView);
    this.partnerWaypointArrow?.update(camView);
  }

  private refreshNpcNameplates(): void {
    const manifest = this.reg().caseManifest;
    const journal = this.reg().getJournal() as Record<string, unknown>;
    for (const [instanceId, state] of this.npcSprites) {
      state.nameplate.setText(npcWorldNameplateLabel(manifest, instanceId, journal));
    }
  }

  /** E-key proximity — player must stand near the NPC (not tap-at-a-distance). */
  private npcInteractRadiusPx(): number {
    return Math.max(NPC_INTERACT_RADIUS_TILES * TILE_SIZE, WORLD_CHARACTER_DISPLAY_PX * 0.55);
  }

  private npcWithinInteractPx(px: number, py: number, rig: Phaser.GameObjects.Container): boolean {
    return Phaser.Math.Distance.Between(px, py, rig.x, rig.y) <= this.npcInteractRadiusPx();
  }

  private playerNearTransit(px: number, py: number): boolean {
    return playerNearZoneTransit(
      px,
      py,
      this.zone,
      this.reg().layerA,
      TRANSIT_INTERACT_TILES
    );
  }

  private transitInteractHint(): string {
    return this.zone === "downtown"
      ? "Travel to Maple Heights — tap arrow or E"
      : "Travel to Downtown — tap arrow or E";
  }

  /** E-key interactions from the player's position (hints use the same helpers). */
  private tryInteractFromPlayer(px: number, py: number): boolean {
    if (this.interactionBlocked()) return false;
    if (this.transitCooldown > 0) return false;
    if (this.viewMode === "interior") {
      if (this.playerNearInteriorExit(px, py)) {
        this.stopPlayerForInteract();
        this.promptInteriorExit();
        return true;
      }
      if (
        this.interiorSpec &&
        this.interiorContentPlan?.showClueMarker &&
        playerNearClueMarker(px, py, this.interiorSpec)
      ) {
        this.stopPlayerForInteract();
        this.registry.events.emit("interior_clue_interact", this.interiorBuildingId);
        return true;
      }
      for (const [, run] of this.npcSprites) {
        if (this.npcWithinInteractPx(px, py, run.rig)) {
          this.stopPlayerForInteract();
          this.openNpcDialog(run.rig.getData("instance_id") as string);
          return true;
        }
      }
      return false;
    }
    if (this.playerNearTransit(px, py)) {
      this.promptZoneTransit();
      return true;
    }
    for (const [, run] of this.npcSprites) {
      if (this.npcWithinInteractPx(px, py, run.rig)) {
        this.stopPlayerForInteract();
        this.openNpcDialog(run.rig.getData("instance_id") as string);
        return true;
      }
    }
    const arrowBid = findBuildingEntryArrowAt(this.buildingEntryArrows, px, py);
    if (arrowBid) {
      this.stopPlayerForInteract();
      this.openBuildingViaEntryArrow(arrowBid);
      return true;
    }
    return false;
  }

  private updateInteract(): void {
    if (this.interactionBlocked()) {
      this.interactHint.setVisible(false);
      return;
    }
    let hint = "";
    const px = this.playerRig.x;
    const py = this.playerRig.y;
    const eDown = this.keyE && Phaser.Input.Keyboard.JustDown(this.keyE);
    if (eDown && this.tryInteractFromPlayer(px, py)) {
      this.interactHint.setVisible(false);
      return;
    }

    if (this.viewMode === "interior") {
      if (this.playerNearInteriorExit(px, py)) {
        hint = "Exit to street — tap arrow or E";
      } else if (
        this.interiorSpec &&
        this.interiorContentPlan?.showClueMarker &&
        playerNearClueMarker(px, py, this.interiorSpec)
      ) {
        hint = "Examine — tap or E";
      }
    } else if (this.playerNearTransit(px, py)) {
      hint = this.transitInteractHint();
    }

    if (!hint) {
      for (const [, run] of this.npcSprites) {
        if (this.npcWithinInteractPx(px, py, run.rig)) {
          hint = `Review — tap or E (${String(run.rig.getData("name") || "Someone")})`;
          break;
        }
      }
    }
    if (!hint) {
      const arrowBid = findBuildingEntryArrowAt(this.buildingEntryArrows, px, py, TILE_SIZE * 1.6);
      if (arrowBid) {
        const permitted = this.buildingEntryArrows.get(arrowBid)?.permitted === true;
        hint = buildingEntryArrowInteractHint(permitted);
      }
    }
    this.interactHint.setVisible(Boolean(hint));
    this.interactHint.setText(hint);
    if (hint) {
      layoutInteractHint(this.interactHint, this.scale.width, this.scale.height);
    }
  }
}
